import dayjs from 'dayjs';
import { APP_RECORD_TYPES, ENTITY_REF_TARGETS, getEntityRefTarget, getDistributionChannels, getChannelLabel, isHiddenRecordType, CONTRAST_TEXT_COLORS, ICONS, COLORS, ITEM_NAMES, PROJECT_NAMES } from 'vmd-types';
import utc from 'dayjs/plugin/utc';

function buildAccessString(obj) {
    const { client, tool, role, projectIds, granularPermissions, } = obj;
    const qp = new URLSearchParams();
    qp.set("tool", tool);
    role && qp.set("role", role);
    if (granularPermissions && granularPermissions.length > 0) {
        granularPermissions.forEach((perm) => {
            qp.append("object", perm.object);
            const ops = perm.operation.split(",");
            ops.forEach((op) => qp.append("operation", op.trim()));
        });
    }
    let query = qp.toString();
    // projectIds packed as a single comma-separated value, deduped and not
    // URL-encoded, to keep the resulting keycloak group name within the
    // 255-char column limit even for many ids
    if (projectIds?.length) {
        const unique = Array.from(new Set(projectIds.map(String)));
        query += `&projectId=${unique.join(",")}`;
    }
    return `pimo-product/${client}?${query}`;
}
function parseArrayParam(raw) {
    if (!raw)
        return [];
    const trimmed = raw.trim();
    try {
        const asJson = JSON.parse(trimmed);
        if (Array.isArray(asJson)) {
            return asJson.map(String);
        }
    }
    catch { }
    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
        const inner = trimmed.slice(1, -1).trim();
        if (!inner)
            return [];
        return inner
            .split(",")
            .map((s) => s.trim().replace(/^"|"$/g, "").replace(/^'|'$/g, ""));
    }
    return trimmed.split(",").map((s) => s.trim());
}
function parsePermission(str) {
    const [path, query = ""] = str.split("?");
    const [product, client] = path.split("/");
    const sp = new URLSearchParams(query);
    const tool = sp.get("tool") ?? "";
    const role = (sp.get("role") ?? "");
    const granularPermissions = [];
    let pendingOps = []; // operations seen before the first object
    let currentObject = null;
    let currentOps = [];
    for (const [key, value] of Array.from(sp.entries())) {
        if (key === "object") {
            if (currentObject) {
                granularPermissions.push({
                    object: currentObject,
                    operation: currentOps.join(","),
                });
            }
            currentObject = value;
            // consume any operations that appeared before this object
            currentOps = pendingOps;
            pendingOps = [];
        }
        else if (key === "operation") {
            if (currentObject) {
                currentOps.push(value);
            }
            else {
                pendingOps.push(value);
            }
        }
    }
    if (currentObject) {
        granularPermissions.push({
            object: currentObject,
            operation: currentOps.join(","),
        });
    }
    // Prefer repeated params; also expand any comma-joined values so both
    // legacy (?projectId=1&projectId=2) and compact (?projectId=1,2) forms work
    let projectIds = sp.getAll("projectId").flatMap((v) => parseArrayParam(v));
    // Fallback: if not repeated, try single-value legacy formats
    if (projectIds?.length === 0) {
        const raw = sp.get("projectId");
        projectIds = parseArrayParam(raw);
    }
    const result = { product, client, tool, role };
    if (projectIds?.length)
        result.projectIds = Array.from(new Set(projectIds.map(String)));
    if (granularPermissions.length > 0) {
        result.granularPermissions = granularPermissions;
    }
    else {
        let operationOps = sp.getAll("operation");
        let object = sp.getAll("object");
        if (operationOps?.length === 0) {
            const raw = sp.get("operation");
            operationOps = parseArrayParam(raw);
        }
        if (object?.length === 0) {
            const raw = sp.get("object");
            object = parseArrayParam(raw);
        }
    }
    return result;
}

function canUserPerformOperation(params) {
    const { userGroups, entityId = "", operation, lowestLevelAccessRequiredToPerformThisOperation, tool, client, } = params;
    let userPermission = undefined;
    for (const group of userGroups ?? []) {
        const permissions = parsePermission(group);
        if (permissions.tool === tool && permissions.client === client) {
            userPermission = permissions;
        }
    }
    if (lowestLevelAccessRequiredToPerformThisOperation) {
        const levels = ["project", "program", "admin"];
        if (levels.indexOf(userPermission?.role ?? "project") <
            levels.indexOf(lowestLevelAccessRequiredToPerformThisOperation)) {
            return false;
        }
    }
    // Admins always have access
    if (userPermission?.role === "admin") {
        return true;
    }
    const hasGranularOrLegacyAccess = (object, op) => {
        if (userPermission?.granularPermissions) {
            const perm = userPermission.granularPermissions.find((p) => p.object === object);
            return (perm?.operation
                .split(",")
                .map((s) => s.trim())
                .includes(op) ?? false);
        }
        return false;
    };
    if (userPermission?.role === "program") {
        if (operation === "read") {
            return true;
        }
        if (operation &&
            params.object &&
            hasGranularOrLegacyAccess(params.object, operation)) {
            return true;
        }
    }
    // Project role with matching entityId and operation, or any write access
    if (userPermission?.role === "project" &&
        operation &&
        userPermission?.projectIds) {
        if (userPermission.projectIds?.some((p) => String(p) === String(entityId)) &&
            params.object &&
            hasGranularOrLegacyAccess(params.object, operation)) {
            return true;
        }
    }
    // Default: no access
    return false;
}

function determineUserRole(user, tool) {
    if (!user) {
        return;
    }
    const userRoles = [];
    (user?.groups ?? []).forEach((group) => {
        const { tool: parsedTool, role } = parsePermission(group);
        if (parsedTool === tool && role != null) {
            userRoles.push(role);
        }
    });
    return determineRole(userRoles);
}
function determineRole(roles) {
    if (roles.includes("admin")) {
        return "admin";
    }
    if (roles.includes("program")) {
        return "program";
    }
    if (roles.includes("project")) {
        return "project";
    }
}

function calculateOverallCompletionDegreeCounts(subentities) {
    const completionDegreeCounts = {};
    for (const entry of subentities?.map((entry) => entry.degreeOfCompletion) ??
        []) {
        if (!entry) {
            continue;
        }
        if (Object.keys(completionDegreeCounts).includes(entry)) {
            completionDegreeCounts[entry]++;
        }
        else {
            completionDegreeCounts[entry] = 1;
        }
    }
    return { completionDegreeCounts };
}

function calculateTotalBudgetDetails(projects) {
    const totalApprovedBudget = projects.reduce((total, project) => {
        return total + (project.approvedBudget ?? 0);
    }, 0);
    const totalSpentBudget = projects.reduce((total, project) => {
        return total + (project.spentBudget ?? 0);
    }, 0);
    const percentageOfBudgetSpent = totalApprovedBudget === 0
        ? 0
        : Math.round((totalSpentBudget / totalApprovedBudget) * 100);
    return {
        percentageOfBudgetSpent: percentageOfBudgetSpent,
        approvedBudget: totalApprovedBudget,
        spentBudget: totalSpentBudget,
    };
}

const STAGE_ORDER = {
    notStarted: 0,
    inProgress: 1,
    readyForSubmission: 2,
    submitted: 3,
};
/**
 * Resolves the configured status name for a review stage. Names are admin-editable,
 * so nothing may compare against a literal like "Submitted".
 *
 * A configuration that predates the four-stage model only has three entries; there
 * the last entry is the terminal one, so `submitted` resolves to it and
 * `readyForSubmission` resolves to nothing rather than to a wrong status.
 *
 * The last entry always means `submitted`, even at a stage count no other stage
 * expects. So a stage whose position would land on that last entry resolves to
 * nothing too, rather than to the submitted name: reaching "in progress" must
 * never read back as "submitted" and lock the review read-only.
 */
function getReviewStageName(configuration, stage) {
    const definitions = configuration?.degreeOfCompletionDefinition ?? [];
    if (definitions.length === 0) {
        return undefined;
    }
    const byOrder = [...definitions].sort((a, b) => a.order - b.order);
    const lastIndex = byOrder.length - 1;
    if (stage === "submitted") {
        return byOrder[lastIndex]?.name || undefined;
    }
    if (STAGE_ORDER[stage] >= lastIndex) {
        return undefined;
    }
    return byOrder[STAGE_ORDER[stage]]?.name || undefined;
}

function countCompletedItemsPerCategory(projects, configuration) {
    const categoryCounts = {};
    if (!configuration.degreeOfCompletionDefinition || !projects?.length) {
        return [];
    }
    const degreeOfCompletionDefinition = getReviewStageName(configuration, "submitted");
    const flattedReportingItemsWithCategory = projects
        .flatMap((p) => p.reportingItems?.map((i) => ({ ...i, category: p.category })))
        .filter(
    // An unresolved name must not match an item with no degree of
    // completion either — both are `undefined`.
    (item) => !!degreeOfCompletionDefinition &&
        item?.degreeOfCompletion === degreeOfCompletionDefinition);
    const existingCategories = configuration.categoryDefinition?.map((cat) => cat.name) ?? [];
    for (const entry of flattedReportingItemsWithCategory?.map((entry) => entry?.category) ?? []) {
        if (!entry || !existingCategories.includes(entry)) {
            continue;
        }
        if (Object.keys(categoryCounts).includes(entry)) {
            categoryCounts[entry]++;
        }
        else {
            categoryCounts[entry] = 1;
        }
    }
    const result = existingCategories.map((category) => ({
        label: category,
        value: categoryCounts[category] ?? 0,
        color: configuration.categoryDefinition?.find((item) => item.name === category)?.color,
    }));
    return result;
}

const countCompletedProjectsOverReportingCycle = (projects, configuration) => {
    if (!projects ||
        projects.length === 0 ||
        !configuration?.degreeOfCompletionDefinition ||
        !configuration?.reportingCycle?.startReportingDate ||
        !configuration?.reportingCycle?.endReportingDate ||
        !configuration?.reportingCycle?.frequency ||
        !configuration?.reportingCycle?.unit) {
        return [];
    }
    const { frequency, unit } = configuration.reportingCycle;
    let startReportingDate = dayjs(configuration.reportingCycle.startReportingDate);
    const endReportingDate = dayjs(configuration.reportingCycle.endReportingDate);
    const completedItemsOverReportingCycle = [];
    while (startReportingDate.isBefore(endReportingDate)) {
        const endReportingCycleDate = startReportingDate.add(frequency, unit);
        const completedItems = projects.filter((item) => {
            const completionDate = dayjs(item._calculated?.completionDate);
            if (!completionDate)
                return false;
            return (completionDate.isAfter(startReportingDate) &&
                !completionDate.isAfter(endReportingCycleDate));
        }).length;
        startReportingDate = startReportingDate.add(frequency, unit);
        completedItemsOverReportingCycle.push({
            category: dayjs(startReportingDate).format("YYYY-MM"),
            count: completedItems,
        });
    }
    return completedItemsOverReportingCycle;
};

function countCompletedProjectsPerCategory(projects, configuration) {
    const categoryCounts = {};
    if (!configuration.degreeOfCompletionDefinition || !projects?.length) {
        return [];
    }
    const completedProjects = projects.filter((project) => project?._calculated?.completionDate);
    const existingCategories = configuration.categoryDefinition?.map((cat) => cat.name) ?? [];
    for (const entry of completedProjects?.map((entry) => entry?.category) ??
        []) {
        if (!entry || !existingCategories.includes(entry)) {
            continue;
        }
        if (Object.keys(categoryCounts).includes(entry)) {
            categoryCounts[entry]++;
        }
        else {
            categoryCounts[entry] = 1;
        }
    }
    const result = existingCategories.map((category) => ({
        label: category,
        value: categoryCounts[category] ?? 0,
        color: configuration.categoryDefinition?.find((item) => item.name === category)?.color,
    }));
    return result;
}

function countCompletionDegreesOfProjects(projects) {
    const completionDegreeCounts = {};
    for (const entry of projects?.map((entry) => entry._calculated?.overallDegreeOfCompletion?.name) ?? []) {
        if (!entry) {
            continue;
        }
        if (Object.keys(completionDegreeCounts).includes(entry)) {
            completionDegreeCounts[entry]++;
        }
        else {
            completionDegreeCounts[entry] = 1;
        }
    }
    return { completionDegreeCounts };
}

/**
 * An App counts as reviewed only when the questionnaire actually carried it
 * there: the Submitted degree alone is not enough, because an OE save can carry
 * a client-supplied `degreeOfCompletion` for a column the OE report only
 * displays. `progress` is recomputed server-side on every tracking-item save,
 * so it is the trustworthy half of the pair.
 */
function isReportingItemReviewed(reportingItem, configuration) {
    const doneName = getReviewStageName(configuration, "submitted");
    if (!doneName || reportingItem?.degreeOfCompletion !== doneName) {
        return false;
    }
    // Without questionnaire blocks nothing ever computes progress, so requiring
    // 100 there would leave those use cases permanently incomplete. Mirrors the
    // block filter in calculateReportingItemProgress.
    const hasQuestionnaire = (configuration?.trackingItemForms?.[0]?.blocks ?? []).some((block) => block.enabled);
    if (!hasQuestionnaire) {
        return true;
    }
    return reportingItem?.progress === 100;
}

function countReviewedBusinessApplications(reportingItems, configuration) {
    const businessApplications = (reportingItems ?? []).filter((item) => item?.basicItem?.recordType === APP_RECORD_TYPES.BUSINESS_APPLICATION);
    const reviewedBusinessApplications = businessApplications.filter((item) => isReportingItemReviewed(item, configuration)).length;
    const totalBusinessApplications = businessApplications.length;
    const reviewedBusinessApplicationsPercent = totalBusinessApplications > 0
        ? Math.round((reviewedBusinessApplications / totalBusinessApplications) * 100)
        : 0;
    return {
        reviewedBusinessApplications,
        totalBusinessApplications,
        reviewedBusinessApplicationsPercent,
    };
}

function countSemaphoreStatusesOfProjects(projects) {
    const statusCounts = {};
    for (const entry of projects?.map((entry) => entry._calculated?.overallSemaphoreStatus?.name) ?? []) {
        if (!entry) {
            continue;
        }
        if (Object.keys(statusCounts).includes(entry)) {
            statusCounts[entry]++;
        }
        else {
            statusCounts[entry] = 1;
        }
    }
    return { statusCounts };
}

function countStatusesOfProjects(projects) {
    const statusCounts = {};
    for (const entry of projects?.map((entry) => entry._calculated?.overallStatus?.name) ?? []) {
        if (!entry) {
            continue;
        }
        if (Object.keys(statusCounts).includes(entry)) {
            statusCounts[entry]++;
        }
        else {
            statusCounts[entry] = 1;
        }
    }
    return { statusCounts };
}

// Translation object for all field labels
const translations = {
    en: {
        ID: "ID",
        Name: "Name",
        Owner: "Owner",
        Description: "Description",
        "Start date": "Start date",
        "End date": "End date",
        Budget: "Approved Budget",
        Severity: "Severity",
        Status: "Status",
        "Review Status": "Review Status",
        Progress: "Progress",
        Comment: "Comment",
        "Management Summary": "Management Summary",
        Challenges: "Challenges",
        Achievements: "Achievements",
        "Next Steps": "Next Steps",
        "Spent Budget": "Spent Budget",
        "Custom ID": "Custom ID",
        Category: "Category",
    },
    de: {
        ID: "ID",
        Name: "Name",
        Owner: "Eigentümer",
        Description: "Beschreibung",
        "Start date": "Startdatum",
        "End date": "Enddatum",
        Budget: "Genehmigtes Budget",
        Severity: "Schweregrad",
        Status: "Status",
        "Review Status": "Prüfstatus",
        Progress: "Fortschritt",
        Comment: "Kommentar",
        "Management Summary": "Management-Zusammenfassung",
        Challenges: "Herausforderungen",
        Achievements: "Erfolge",
        "Next Steps": "Nächste Schritte",
        "Spent Budget": "Ausgegebenes Budget",
        "Custom ID": "Benutzerdefinierte ID",
        Category: "Kategorie",
    },
};
// Function to get translated label
const label = (key, language = "en") => {
    return (translations[language][key] || key);
};
// Function to get audit configuration with language support
const getAuditConfig = (language = "en") => ({
    id: "",
    useCase: "audit",
    projectName: "Finding",
    itemName: "Measure",
    isPublished: false,
    isReportingEnabled: false,
    trackingItemsApplyToAllProjects: false,
    reportingCycle: {
        frequency: 1,
        unit: "week",
        weeklyReportingDay: "0",
    },
    fieldsDefinition: [
        {
            label: label("ID", language),
            fieldName: "customId",
            enabled: true,
            isEditable: false,
            type: "Text",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Name", language),
            fieldName: "name",
            enabled: true,
            isEditable: false,
            type: "Text",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Owner", language),
            fieldName: "owner",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Description", language),
            fieldName: "description",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Start date", language),
            fieldName: "startDate",
            enabled: true,
            isEditable: true,
            type: "Date",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("End date", language),
            fieldName: "endDate",
            enabled: true,
            isEditable: true,
            type: "Date",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Budget", language),
            fieldName: "approvedBudget",
            enabled: false,
            isEditable: true,
            type: "Number",
            purpose: "projectBasic",
            isMandatory: false,
        },
        {
            label: label("Severity", language),
            fieldName: "category",
            enabled: true,
            isEditable: true,
            type: "Dropdown",
            purpose: "projectBasic",
            isMandatory: true,
            fieldInConfiguration: "categoryDefinition",
        },
        {
            label: "Audit Type",
            fieldName: "secondCategory",
            enabled: true,
            isEditable: true,
            type: "Dropdown",
            purpose: "projectBasic",
            isMandatory: false,
            fieldInConfiguration: "secondCategoryDefinition",
        },
        {
            label: "Audit Title",
            fieldName: "freeTextFieldOne",
            enabled: true,
            isEditable: true,
            type: "Number",
            purpose: "projectBasic",
            isMandatory: false,
        },
        {
            label: label("ID", language),
            fieldName: "customId",
            enabled: true,
            isEditable: false,
            type: "Text",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: label("Name", language),
            fieldName: "name",
            enabled: true,
            isEditable: false,
            type: "Text",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: label("Owner", language),
            fieldName: "owner",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: label("Description", language),
            fieldName: "description",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: label("Start date", language),
            fieldName: "startDate",
            enabled: true,
            isEditable: true,
            type: "Date",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: label("End date", language),
            fieldName: "endDate",
            enabled: true,
            isEditable: true,
            type: "Date",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: "Team",
            fieldName: "dropdownFieldOne",
            enabled: false,
            isEditable: true,
            type: "Dropdown",
            purpose: "itemBasic",
            isMandatory: false,
            fieldInConfiguration: "dropdownFieldOneDefinition",
        },
        {
            label: label("Status", language),
            fieldName: "status",
            enabled: true,
            isEditable: true,
            type: "Dropdown",
            purpose: "itemReporting",
            isMandatory: true,
            fieldInConfiguration: "statusDefinition",
        },
        {
            label: label("Review Status", language),
            fieldName: "completion",
            enabled: true,
            isEditable: true,
            type: "Dropdown",
            purpose: "itemReporting",
            isMandatory: true,
            fieldInConfiguration: "degreeOfCompletionDefinition",
        },
        {
            label: label("Progress", language),
            fieldName: "progress",
            enabled: true,
            isEditable: true,
            type: "Number",
            purpose: "itemReporting",
            isMandatory: true,
        },
        {
            label: label("Comment", language),
            fieldName: "comment",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "itemReporting",
            isMandatory: false,
        },
        {
            label: "Attachments",
            fieldName: "attachments",
            enabled: true,
            isEditable: true,
            type: "Attachment",
            purpose: "itemReporting",
            isMandatory: false,
        },
        {
            label: label("Management Summary", language),
            fieldName: "managementSummary",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "projectReporting",
            isMandatory: false,
        },
        {
            label: label("Challenges", language),
            fieldName: "challenges",
            enabled: false,
            isEditable: true,
            type: "Text",
            purpose: "projectReporting",
            isMandatory: false,
        },
        {
            label: label("Achievements", language),
            fieldName: "achievements",
            enabled: false,
            isEditable: true,
            type: "Text",
            purpose: "projectReporting",
            isMandatory: false,
        },
        {
            label: label("Next Steps", language),
            fieldName: "nextSteps",
            enabled: false,
            isEditable: true,
            type: "Text",
            purpose: "projectReporting",
            isMandatory: false,
        },
        {
            label: label("Spent Budget", language),
            fieldName: "spentBudget",
            enabled: false,
            isEditable: true,
            type: "Number",
            purpose: "projectReporting",
            isMandatory: false,
        },
    ],
    dropdownFieldOneDefinition: [
        {
            name: "",
            label: "Choose a label for internal",
            placeholder: "",
            color: "#ccc",
            order: 0,
        },
        {
            name: "",
            label: "Choose a label for external",
            placeholder: "",
            color: "#ccc",
            order: 1,
        },
        {
            name: "",
            label: "Choose a label for medium severity",
            placeholder: "",
            color: "#ccc",
            order: 2,
        },
        {
            name: "",
            label: "Choose a label for high severity",
            placeholder: "",
            color: "#ccc",
            order: 3,
        },
        {
            name: "",
            label: "Choose a label for critical severity",
            placeholder: "",
            color: "#ccc",
            order: 4,
        },
    ],
    degreeOfCompletionDefinition: [
        {
            name: "Not Started",
            color: "#D9D9D9",
            label: "Choose a label for progress not yet started",
            placeholder: "Not Started",
            order: 0,
        },
        {
            name: "In Progress",
            color: "#087DFA",
            label: "Choose a label for in progress",
            placeholder: "In Progress",
            order: 1,
        },
        {
            name: "Done",
            color: "#4CAF50",
            label: "Choose a label for done",
            placeholder: "Done",
            order: 2,
        },
    ],
    categoryDefinition: [
        {
            name: "Very Low",
            label: "Choose a label for very low severity",
            color: "#f4fc0cff",
            placeholder: "Very Low",
            order: 0,
        },
        {
            name: "Low",
            label: "Choose a label for low severity",
            color: "#ffecb3",
            placeholder: "Low",
            order: 1,
        },
        {
            name: "Medium",
            label: "Choose a label for medium severity",
            color: "#ffe082",
            placeholder: "Medium",
            order: 2,
        },
        {
            name: "High",
            label: "Choose a label for high severity",
            color: "#f4870aff",
            placeholder: "High",
            order: 3,
        },
        {
            name: "Critical",
            label: "Choose a label for critical severity",
            color: "#ef3726ff",
            placeholder: "Critical",
            order: 4,
        },
    ],
    secondCategoryDefinition: [
        {
            name: "internal",
            label: "Choose a label for internal",
            placeholder: "",
            order: 0,
        },
        {
            name: "external",
            label: "Choose a label for external",
            placeholder: "",
            order: 1,
        },
        {
            name: "forensic",
            label: "Choose a label for forensic",
            placeholder: "",
            order: 2,
        },
        {
            name: "compliance",
            label: "Choose a label for compliance",
            placeholder: "",
            order: 3,
        },
        {
            name: "operational",
            label: "Choose a label for operational",
            placeholder: "",
            order: 4,
        },
    ],
    statusDefinition: [
        {
            name: "On Track",
            color: "#00C853",
            label: "Choose a label for On‑Track items",
            placeholder: "On Track",
            order: 0,
        },
        {
            name: "At Risk",
            color: "#F89500",
            label: "Choose a label for At‑Risk items",
            placeholder: "At Risk",
            order: 1,
        },
        {
            name: "Critical",
            color: "#FF0000",
            label: "Choose a label for Critical items",
            placeholder: "Critical",
            order: 2,
        },
    ],
});
// Function to get PMO configuration with language support
const getPmoConfig = (language = "en") => ({
    id: "",
    useCase: "pmo",
    projectName: "Project",
    itemName: "Deliverable",
    isPublished: false,
    isReportingEnabled: false,
    trackingItemsApplyToAllProjects: true,
    statusReportingLevel: "category",
    reportingCycle: {
        frequency: 1,
        unit: "week",
        weeklyReportingDay: "0",
    },
    secondCategoryDefinition: [
        {
            name: "",
            label: "Choose a label for internal",
            placeholder: "",
            order: 0,
        },
        {
            name: "",
            label: "Choose a label for external",
            placeholder: "",
            order: 1,
        },
        {
            name: "",
            label: "Choose a label for medium severity",
            placeholder: "",
            order: 2,
        },
        {
            name: "",
            label: "Choose a label for high severity",
            placeholder: "",
            order: 3,
        },
        {
            name: "",
            label: "Choose a label for critical severity",
            placeholder: "",
            order: 4,
        },
    ],
    fieldsDefinition: [
        {
            label: label("Custom ID", language),
            fieldName: "customId",
            enabled: true,
            isEditable: false,
            type: "Text",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Name", language),
            fieldName: "name",
            enabled: true,
            isEditable: false,
            type: "Text",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Owner", language),
            fieldName: "owner",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Description", language),
            fieldName: "description",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Start date", language),
            fieldName: "startDate",
            enabled: true,
            isEditable: true,
            type: "Date",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("End date", language),
            fieldName: "endDate",
            enabled: true,
            isEditable: true,
            type: "Date",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Budget", language),
            fieldName: "approvedBudget",
            enabled: true,
            isEditable: true,
            type: "Number",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: "Category",
            fieldName: "category",
            enabled: false,
            isEditable: true,
            type: "Dropdown",
            purpose: "projectBasic",
            isMandatory: false,
            fieldInConfiguration: "categoryDefinition",
        },
        {
            label: label("ID", language),
            fieldName: "customId",
            enabled: true,
            isEditable: false,
            type: "Text",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: label("Name", language),
            fieldName: "name",
            enabled: true,
            isEditable: false,
            type: "Text",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: label("Owner", language),
            fieldName: "owner",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: label("Description", language),
            fieldName: "description",
            enabled: false,
            isEditable: true,
            type: "Text",
            purpose: "itemBasic",
            isMandatory: false,
        },
        {
            label: label("Start date", language),
            fieldName: "startDate",
            enabled: true,
            isEditable: true,
            type: "Date",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: label("End date", language),
            fieldName: "endDate",
            enabled: true,
            isEditable: true,
            type: "Date",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: "Category",
            fieldName: "dropdownFieldOne",
            enabled: false,
            isEditable: true,
            type: "Dropdown",
            purpose: "itemBasic",
            isMandatory: false,
            fieldInConfiguration: "dropdownFieldOneDefinition",
        },
        {
            label: label("Status", language),
            fieldName: "status",
            enabled: true,
            isEditable: true,
            type: "Dropdown",
            purpose: "itemReporting",
            isMandatory: true,
            fieldInConfiguration: "semaphoreStatusDefinition",
        },
        {
            label: label("Review Status", language),
            fieldName: "completion",
            enabled: true,
            isEditable: true,
            type: "Dropdown",
            purpose: "itemReporting",
            isMandatory: true,
            fieldInConfiguration: "degreeOfCompletionDefinition",
        },
        {
            label: label("Progress", language),
            fieldName: "progress",
            enabled: true,
            isEditable: true,
            type: "Number",
            purpose: "itemReporting",
            isMandatory: true,
        },
        {
            label: label("Comment", language),
            fieldName: "comment",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "itemReporting",
            isMandatory: false,
        },
        {
            label: "Attachments",
            fieldName: "attachments",
            enabled: false,
            isEditable: true,
            type: "Attachment",
            purpose: "itemReporting",
            isMandatory: false,
        },
        {
            label: label("Management Summary", language),
            fieldName: "managementSummary",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "projectReporting",
            isMandatory: true,
        },
        {
            label: label("Challenges", language),
            fieldName: "challenges",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "projectReporting",
            isMandatory: true,
        },
        {
            label: label("Achievements", language),
            fieldName: "achievements",
            enabled: false,
            isEditable: true,
            type: "Text",
            purpose: "projectReporting",
            isMandatory: false,
        },
        {
            label: label("Next Steps", language),
            fieldName: "nextSteps",
            enabled: false,
            isEditable: true,
            type: "Text",
            purpose: "projectReporting",
            isMandatory: false,
        },
        {
            label: label("Spent Budget", language),
            fieldName: "spentBudget",
            enabled: true,
            isEditable: true,
            type: "Number",
            purpose: "projectReporting",
            isMandatory: true,
        },
    ],
    degreeOfCompletionDefinition: [
        {
            name: "Not Started",
            color: "#D9D9D9",
            label: "Choose a label for progress not yet started",
            placeholder: "Not Started",
            order: 0,
        },
        {
            name: "In Progress",
            color: "#FFB74D",
            label: "Choose a label for in progress",
            placeholder: "In Progress",
            order: 1,
        },
        {
            name: "Done",
            color: "#4CAF50",
            label: "Choose a label for done",
            placeholder: "Done",
            order: 2,
        },
    ],
    dropdownFieldOneDefinition: [
        {
            name: "",
            label: "Choose a label for item 1",
            placeholder: "",
            color: "#ccc",
            order: 0,
        },
        {
            name: "",
            label: "Choose a label for item 2",
            placeholder: "",
            color: "#ccc",
            order: 1,
        },
        {
            name: "",
            label: "Choose a label for item 3",
            placeholder: "",
            color: "#ccc",
            order: 2,
        },
        {
            name: "",
            label: "Choose a label for item 4",
            placeholder: "",
            color: "#ccc",
            order: 3,
        },
        {
            name: "",
            label: "Choose a label for item 5",
            placeholder: "",
            color: "#ccc",
            order: 4,
        },
    ],
    categoryDefinition: [
        {
            name: "",
            label: "Choose a label for item 1",
            color: "#f4fc0cff",
            placeholder: "Very Low",
            order: 0,
        },
        {
            name: "",
            label: "Choose a label for item 2",
            color: "#ffecb3",
            placeholder: "Low",
            order: 1,
        },
        {
            name: "",
            label: "Choose a label for item 3",
            color: "#ffe082",
            placeholder: "Medium",
            order: 2,
        },
        {
            name: "",
            label: "Choose a label for item 4",
            color: "#f4870aff",
            placeholder: "High",
            order: 3,
        },
        {
            name: "",
            label: "Choose a label for item 5",
            color: "#ef3726ff",
            placeholder: "Critical",
            order: 4,
        },
    ],
    statusDefinition: [
        {
            name: "On Track",
            color: "#00C853",
            label: "Choose a label for On‑Track items",
            placeholder: "On Track",
            order: 0,
        },
        {
            name: "At Risk",
            color: "#F89500",
            label: "Choose a label for At‑Risk items",
            placeholder: "At Risk",
            order: 1,
        },
        {
            name: "Critical",
            color: "#FF0000",
            label: "Choose a label for Critical items",
            placeholder: "Critical",
            order: 2,
        },
    ],
    semaphoreStatusDefinition: [
        {
            name: "Time",
            low: {
                name: "On Track",
                color: "green",
                order: 0,
            },
            medium: {
                name: "At Risk",
                color: "yellow",
                order: 1,
            },
            high: {
                name: "Critical",
                color: "red",
                order: 2,
            },
            label: "Choose a label for On‑Track items",
            placeholder: "Time",
            order: 0,
        },
        {
            name: "Budget",
            low: {
                name: "On Track",
                color: "green",
                order: 0,
            },
            medium: {
                name: "At Risk",
                color: "yellow",
                order: 1,
            },
            high: {
                name: "Critical",
                color: "red",
                order: 2,
            },
            label: "Choose a label for At‑Risk items",
            placeholder: "Budget",
            order: 1,
        },
        {
            name: "Scope",
            low: {
                name: "On Track",
                color: "green",
                order: 0,
            },
            medium: {
                name: "At Risk",
                color: "yellow",
                order: 1,
            },
            high: {
                name: "Critical",
                color: "red",
                order: 2,
            },
            label: "Choose a label for Critical items",
            placeholder: "Scope",
            order: 2,
        },
    ],
});
// Function to get rollout configuration with language support
const getRolloutConfig = (language = "en") => ({
    id: "",
    useCase: "rollout",
    reportingCycle: {
        frequency: 1,
        unit: "week",
        weeklyReportingDay: "0",
    },
    isPublished: false,
    isReportingEnabled: false,
    trackingItemsApplyToAllProjects: true,
    projectName: "Project",
    itemName: "Activity",
    secondCategoryDefinition: [
        {
            name: "",
            label: "Choose a label for internal",
            placeholder: "",
            order: 0,
        },
        {
            name: "",
            label: "Choose a label for external",
            placeholder: "",
            order: 1,
        },
        {
            name: "",
            label: "Choose a label for medium severity",
            placeholder: "",
            order: 2,
        },
        {
            name: "",
            label: "Choose a label for high severity",
            placeholder: "",
            order: 3,
        },
        {
            name: "",
            label: "Choose a label for critical severity",
            placeholder: "",
            order: 4,
        },
    ],
    fieldsDefinition: [
        {
            label: label("ID", language),
            fieldName: "id",
            enabled: false,
            isEditable: true,
            type: "Text",
            purpose: "projectBasic",
            isMandatory: false,
        },
        {
            label: label("ID", language),
            fieldName: "customId",
            enabled: true,
            isEditable: false,
            type: "Text",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Name", language),
            fieldName: "name",
            enabled: true,
            isEditable: false,
            type: "Text",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Owner", language),
            fieldName: "owner",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Description", language),
            fieldName: "description",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Start date", language),
            fieldName: "startDate",
            enabled: true,
            isEditable: true,
            type: "Date",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("End date", language),
            fieldName: "endDate",
            enabled: true,
            isEditable: true,
            type: "Date",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Budget", language),
            fieldName: "approvedBudget",
            enabled: true,
            isEditable: true,
            type: "Number",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: "Category",
            fieldName: "category",
            enabled: false,
            isEditable: true,
            type: "Dropdown",
            purpose: "projectBasic",
            isMandatory: false,
            fieldInConfiguration: "categoryDefinition",
        },
        {
            label: label("ID", language),
            fieldName: "customId",
            enabled: true,
            isEditable: false,
            type: "Text",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: label("Name", language),
            fieldName: "name",
            enabled: true,
            isEditable: false,
            type: "Text",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: label("Owner", language),
            fieldName: "owner",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: label("Description", language),
            fieldName: "description",
            enabled: false,
            isEditable: true,
            type: "Text",
            purpose: "itemBasic",
            isMandatory: false,
        },
        {
            label: label("Start date", language),
            fieldName: "startDate",
            enabled: true,
            isEditable: true,
            type: "Date",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: label("End date", language),
            fieldName: "endDate",
            enabled: true,
            isEditable: true,
            type: "Date",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: "Category",
            fieldName: "dropdownFieldOne",
            enabled: false,
            isEditable: true,
            type: "Dropdown",
            purpose: "itemBasic",
            isMandatory: false,
            fieldInConfiguration: "dropdownFieldOneDefinition",
        },
        {
            label: label("Status", language),
            fieldName: "status",
            enabled: true,
            isEditable: true,
            type: "Dropdown",
            purpose: "itemReporting",
            isMandatory: true,
            fieldInConfiguration: "statusDefinition",
        },
        {
            label: label("Review Status", language),
            fieldName: "completion",
            enabled: true,
            isEditable: true,
            type: "Dropdown",
            purpose: "itemReporting",
            isMandatory: true,
            fieldInConfiguration: "degreeOfCompletionDefinition",
        },
        {
            label: label("Progress", language),
            fieldName: "progress",
            enabled: true,
            isEditable: true,
            type: "Number",
            purpose: "itemReporting",
            isMandatory: true,
        },
        {
            label: label("Comment", language),
            fieldName: "comment",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "itemReporting",
            isMandatory: false,
        },
        {
            label: "Attachments",
            fieldName: "attachments",
            enabled: true,
            isEditable: true,
            type: "Attachment",
            purpose: "itemReporting",
            isMandatory: false,
        },
        {
            label: label("Management Summary", language),
            fieldName: "managementSummary",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "projectReporting",
            isMandatory: true,
        },
        {
            label: label("Challenges", language),
            fieldName: "challenges",
            enabled: false,
            isEditable: true,
            type: "Text",
            purpose: "projectReporting",
            isMandatory: false,
        },
        {
            label: label("Achievements", language),
            fieldName: "achievements",
            enabled: false,
            isEditable: true,
            type: "Text",
            purpose: "projectReporting",
            isMandatory: false,
        },
        {
            label: label("Next Steps", language),
            fieldName: "nextSteps",
            enabled: false,
            isEditable: true,
            type: "Text",
            purpose: "projectReporting",
            isMandatory: false,
        },
        {
            label: label("Spent Budget", language),
            fieldName: "spentBudget",
            enabled: true,
            isEditable: true,
            type: "Number",
            purpose: "projectReporting",
            isMandatory: true,
        },
    ],
    dropdownFieldOneDefinition: [
        {
            name: "",
            label: "Choose a label for item 1",
            placeholder: "",
            color: "#ccc",
            order: 0,
        },
        {
            name: "",
            label: "Choose a label for item 2",
            placeholder: "",
            color: "#ccc",
            order: 1,
        },
        {
            name: "",
            label: "Choose a label for item 3",
            placeholder: "",
            color: "#ccc",
            order: 2,
        },
        {
            name: "",
            label: "Choose a label for item 4",
            placeholder: "",
            color: "#ccc",
            order: 3,
        },
        {
            name: "",
            label: "Choose a label for item 5",
            placeholder: "",
            color: "#ccc",
            order: 4,
        },
    ],
    categoryDefinition: [
        {
            name: "",
            label: "Choose a label for item 1",
            color: "#f4fc0cff",
            placeholder: "Very Low",
            order: 0,
        },
        {
            name: "",
            label: "Choose a label for item 2",
            color: "#ffecb3",
            placeholder: "Low",
            order: 1,
        },
        {
            name: "",
            label: "Choose a label for item 3",
            color: "#ffe082",
            placeholder: "Medium",
            order: 2,
        },
        {
            name: "",
            label: "Choose a label for item 4",
            color: "#f4870aff",
            placeholder: "High",
            order: 3,
        },
        {
            name: "",
            label: "Choose a label for item 5",
            color: "#ef3726ff",
            placeholder: "Critical",
            order: 4,
        },
    ],
    degreeOfCompletionDefinition: [
        {
            name: "Not Started",
            color: "#D9D9D9",
            label: "Choose a label for progress not yet started",
            placeholder: "Not Started",
            order: 0,
        },
        {
            name: "In Progress",
            color: "#FFB74D",
            label: "Choose a label for in progress",
            placeholder: "In Progress",
            order: 1,
        },
        {
            name: "Done",
            color: "#4CAF50",
            label: "Choose a label for done",
            placeholder: "Done",
            order: 2,
        },
    ],
    statusDefinition: [
        {
            name: "On Track",
            color: "#00C853",
            label: "Choose a label for On‑Track items",
            placeholder: "On Track",
            order: 0,
        },
        {
            name: "At Risk",
            color: "#F89500",
            label: "Choose a label for At‑Risk items",
            placeholder: "At Risk",
            order: 1,
        },
        {
            name: "Critical",
            color: "#FF0000",
            label: "Choose a label for Critical items",
            placeholder: "Critical",
            order: 2,
        },
    ],
});
const getAssessmentConfig = (language = "en") => ({
    id: "",
    useCase: "assessment",
    reportingCycle: {
        frequency: 1,
        unit: "week",
        weeklyReportingDay: "0",
    },
    isPublished: false,
    isReportingEnabled: false,
    trackingItemsApplyToAllProjects: true,
    projectName: "Company",
    itemName: "Question",
    secondCategoryDefinition: [
        {
            name: "",
            label: "Choose a label for internal",
            placeholder: "",
            order: 0,
        },
        {
            name: "",
            label: "Choose a label for external",
            placeholder: "",
            order: 1,
        },
        {
            name: "",
            label: "Choose a label for medium severity",
            placeholder: "",
            order: 2,
        },
        {
            name: "",
            label: "Choose a label for high severity",
            placeholder: "",
            order: 3,
        },
        {
            name: "",
            label: "Choose a label for critical severity",
            placeholder: "",
            order: 4,
        },
    ],
    fieldsDefinition: [
        {
            label: label("ID", language),
            fieldName: "id",
            enabled: false,
            isEditable: true,
            type: "Text",
            purpose: "projectBasic",
            isMandatory: false,
        },
        {
            label: label("Name", language),
            fieldName: "name",
            enabled: true,
            isEditable: false,
            type: "Text",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Owner", language),
            fieldName: "owner",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Description", language),
            fieldName: "description",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "projectBasic",
            isMandatory: true,
        },
        {
            label: label("Start date", language),
            fieldName: "startDate",
            enabled: false,
            isEditable: true,
            type: "Date",
            purpose: "projectBasic",
            isMandatory: false,
        },
        {
            label: label("End date", language),
            fieldName: "endDate",
            enabled: false,
            isEditable: true,
            type: "Date",
            purpose: "projectBasic",
            isMandatory: false,
        },
        {
            label: label("Budget", language),
            fieldName: "approvedBudget",
            enabled: false,
            isEditable: true,
            type: "Number",
            purpose: "projectBasic",
            isMandatory: false,
        },
        {
            label: label("ID", language),
            fieldName: "customId",
            enabled: true,
            isEditable: false,
            type: "Text",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: label("Name", language),
            fieldName: "name",
            enabled: true,
            isEditable: false,
            type: "Text",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: label("Owner", language),
            fieldName: "owner",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: label("Description", language),
            fieldName: "description",
            enabled: false,
            isEditable: true,
            type: "Text",
            purpose: "itemBasic",
            isMandatory: false,
        },
        {
            label: label("Start date", language),
            fieldName: "startDate",
            enabled: true,
            isEditable: true,
            type: "Date",
            purpose: "itemBasic",
            isMandatory: false,
        },
        {
            label: label("End date", language),
            fieldName: "endDate",
            enabled: true,
            isEditable: true,
            type: "Date",
            purpose: "itemBasic",
            isMandatory: false,
        },
        {
            label: "Answer",
            fieldName: "answer",
            enabled: true,
            isEditable: true,
            type: "Dropdown",
            purpose: "itemReporting",
            isMandatory: true,
            fieldInConfiguration: "answerDefinition",
        },
        {
            label: label("Progress", language),
            fieldName: "progress",
            enabled: false,
            isEditable: false,
            type: "Number",
            purpose: "itemReporting",
            isMandatory: false,
        },
        {
            label: label("Comment", language),
            fieldName: "comment",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "itemReporting",
            isMandatory: false,
        },
        {
            label: "Attachments",
            fieldName: "attachments",
            enabled: true,
            isEditable: true,
            type: "Attachment",
            purpose: "itemReporting",
            isMandatory: false,
        },
        {
            label: label("Management Summary", language),
            fieldName: "managementSummary",
            enabled: true,
            isEditable: true,
            type: "Text",
            purpose: "projectReporting",
            isMandatory: true,
        },
        {
            label: label("Challenges", language),
            fieldName: "challenges",
            enabled: false,
            isEditable: true,
            type: "Text",
            purpose: "projectReporting",
            isMandatory: false,
        },
        {
            label: label("Achievements", language),
            fieldName: "achievements",
            enabled: false,
            isEditable: true,
            type: "Text",
            purpose: "projectReporting",
            isMandatory: false,
        },
        {
            label: label("Next Steps", language),
            fieldName: "nextSteps",
            enabled: false,
            isEditable: true,
            type: "Text",
            purpose: "projectReporting",
            isMandatory: false,
        },
        {
            label: label("Spent Budget", language),
            fieldName: "spentBudget",
            enabled: false,
            isEditable: false,
            type: "Number",
            purpose: "projectReporting",
            isMandatory: false,
        },
    ],
    answerDefinition: [
        {
            name: "option1",
            label: "Choose a label for option 1",
            placeholder: "",
            order: 0,
        },
        {
            name: "option2",
            label: "Choose a label for option 2",
            placeholder: "",
            order: 1,
        },
        {
            name: "option3",
            label: "Choose a label for option 3",
            placeholder: "",
            order: 2,
        },
    ],
});
const getDistributionConfig = (language = "en") => {
    const base = getRolloutConfig(language);
    const fields = base.fieldsDefinition ?? [];
    const hasAppAttachments = fields.some((f) => f.fieldName === "attachments" && f.purpose === "itemReporting");
    const fieldsWithAttachments = fields.map((f) => {
        if (f.fieldName === "attachments" && f.purpose === "itemReporting") {
            return { ...f, enabled: true };
        }
        if ((f.fieldName === "startDate" || f.fieldName === "endDate") &&
            f.purpose === "itemBasic") {
            return { ...f, isMandatory: false };
        }
        if (f.fieldName === "customId" && f.purpose === "projectBasic") {
            return {
                ...f,
                isMandatory: false,
                isEditable: true,
                label: label("Custom ID", language),
            };
        }
        return f;
    });
    // Master data fields specific to the DC System / distribution use case's BasicItem
    // entity. They already exist on the BasicItem entity and are rendered unconditionally
    // in the app forms, but were never exposed in the "Master Data DC System" settings —
    // adding them here lets admins enable/disable and mark them mandatory like any other field.
    const hasMasterDataFields = fields.some((f) => f.fieldName === "recordType" && f.purpose === "itemBasic");
    const distributionMasterDataFields = [
        {
            label: "Record Type",
            fieldName: "recordType",
            enabled: true,
            isEditable: true,
            type: "Dropdown",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: "Criticality",
            fieldName: "criticality",
            enabled: true,
            isEditable: true,
            type: "Dropdown",
            purpose: "itemBasic",
            isMandatory: false,
        },
        {
            label: "Life Cycle",
            fieldName: "lifeCycle",
            enabled: true,
            isEditable: true,
            type: "Dropdown",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: "Has Contracts",
            fieldName: "hasContracts",
            enabled: true,
            isEditable: true,
            type: "Logic",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: "Distribution Channel",
            fieldName: "distributionChannel",
            enabled: true,
            isEditable: true,
            type: "Multiselect",
            purpose: "itemReporting",
            isMandatory: false,
            fieldInConfiguration: "distributionChannelDefinition",
        },
        {
            label: "DC Relevance",
            fieldName: "dcRelevance",
            enabled: true,
            isEditable: true,
            type: "Dropdown",
            purpose: "itemReporting",
            isMandatory: false,
            fieldInConfiguration: "dcRelevanceDefinition",
        },
        {
            label: "Process Steps",
            fieldName: "processSteps",
            enabled: true,
            isEditable: true,
            type: "Multiselect",
            purpose: "itemBasic",
            isMandatory: false,
        },
        {
            label: "Application Capabilities",
            fieldName: "applicationCapabilities",
            enabled: true,
            isEditable: true,
            type: "Multiselect",
            purpose: "itemBasic",
            isMandatory: true,
        },
        {
            label: "Technical Exposure",
            fieldName: "technicalExposure",
            enabled: true,
            isEditable: true,
            type: "Multiselect",
            purpose: "itemBasic",
            isMandatory: true,
        },
    ];
    return {
        ...base,
        useCase: "distribution",
        projectName: "OE",
        itemName: "DC System",
        // Distribution reviews are handed in explicitly, so they need a stage
        // between "everything filled" and "submitted". The other use cases keep
        // their three-stage list.
        degreeOfCompletionDefinition: [
            {
                name: "Not Started",
                color: "#D9D9D9",
                label: "Choose a label for progress not yet started",
                placeholder: "Not Started",
                order: 0,
            },
            {
                name: "In Progress",
                color: "#F89500",
                label: "Choose a label for in progress",
                placeholder: "In Progress",
                order: 1,
            },
            {
                name: "Ready to Submit",
                color: "#4CAF50",
                label: "Choose a label for ready for submission",
                placeholder: "Ready to Submit",
                order: 2,
            },
            {
                name: "Submitted",
                color: "#673AB7",
                label: "Choose a label for submitted",
                placeholder: "Submitted",
                order: 3,
            },
        ],
        distributionChannelDefinition: [],
        dcRelevanceDefinition: ["Yes", "No"],
        trackingItemForms: [],
        fieldsDefinition: [
            ...(hasAppAttachments
                ? fieldsWithAttachments
                : [
                    ...fieldsWithAttachments,
                    {
                        label: "Supporting Documents",
                        fieldName: "attachments",
                        enabled: true,
                        type: "Attachment",
                        purpose: "itemReporting",
                    },
                ]),
            ...(hasMasterDataFields ? [] : distributionMasterDataFields),
        ],
    };
};
// Backward compatibility exports (default to English)
const AUDIT_CONFIG = getAuditConfig("en");
const PMO_CONFIG = getPmoConfig("en");
const ROLLOUT_CONFIG = getRolloutConfig("en");
const ASSESSMENT_CONFIG = getAssessmentConfig("en");
const DISTRIBUTION_CONFIG = getDistributionConfig("en");
const DEFAULT_CONFIG = getAuditConfig("en");
const getDefaultConfig = getAuditConfig;

const projectLimits = {
    name: 100,
    owner: 30,
    approvedBudget: 15,
    spentBudget: 15,
    description: 1500,
};
const basicItemLimits = {
    customId: 15,
    description: 1500,
    budget: 15,
    comment: 750,
};
const CHARACTER_LIMITATIONS = {
    programName: 100,
    programOwner: 30,
    programDescription: 1500,
    projectName: projectLimits.name,
    projectOwner: projectLimits.owner,
    projectDescription: projectLimits.description,
    approvedBudget: projectLimits.approvedBudget,
    "projects.{idx}.name": projectLimits.name,
    "projects.{idx}.owner": projectLimits.owner,
    "projects.{idx}.approvedBudget": projectLimits.approvedBudget,
    "projects.{idx}.description": projectLimits.description,
    itemCustomId: basicItemLimits.customId,
    itemDescription: basicItemLimits.description,
    itemComment: basicItemLimits.comment,
    "basicItems.{idx}.customId": basicItemLimits.customId,
    "basicItems.{idx}.description": basicItemLimits.description,
    "basicItems.{idx}.budget": basicItemLimits.budget,
    "basicItems.{idx}.comment": basicItemLimits.comment,
    "statusDefinition.name": 15,
    "degreeOfCompletionDefinition.name": 25,
    "customFields.label": 15,
    "customFields.options.name": 15,
    managementSummary: 1500,
    challenges: 1500,
    achievements: 1500,
    nextSteps: 1500,
    spentBudget: projectLimits.spentBudget,
    "reportingItems.{idx}.comment": 1500,
    "customFields.Select": 15,
    "customFields.Text": 100,
    "customFields.Number": 15,
    "customFields.{idx}.label": 15,
    "customFields.{idx}.options.{idx}.name": 15,
    "semaphoreStatusDefinition.{idx}.name": 15,
    "answerDefinition.{idx}.name": 15,
    "statusDefinition.{idx}.name": 15,
    "degreeOfCompletionDefinition.{idx}.name": 25,
    "trackingItemForms.{idx}.blocks.{idx}.title": 100,
    "trackingItemForms.{idx}.blocks.{idx}.questions.{idx}.label": 200,
    "trackingItemForms.{idx}.blocks.{idx}.questions.{idx}.options.{idx}.label": 100,
};

const AUDIT_VIEW_CONFIG = {
    dashboard: {
        canEdit: true,
        isAiEnabled: false,
        type: "dashboard",
        displayTabs: false,
        tabs: [
            {
                id: 0,
                title: "Dashboard",
                rows: [
                    {
                        id: 0,
                        columnCount: 1,
                        columns: [{ columnIndex: 0, componentId: "titleComponent" }],
                    },
                    {
                        id: 1,
                        columnCount: 4,
                        columns: [
                            { columnIndex: 0, componentId: "statusComponent" },
                            { columnIndex: 1, componentId: "numberOfProjectsComponent" },
                            {
                                columnIndex: 2,
                                componentId: "numberOfTrackingItemsComponent",
                            },
                            { columnIndex: 3, componentId: "numberOfItemsDueComponent" },
                        ],
                    },
                    {
                        id: 2,
                        columnCount: 3,
                        columns: [
                            { columnIndex: 0, componentId: "projectTeamComponent" },
                            { columnIndex: 1, componentId: "projectDurationComponent" },
                            { columnIndex: 2, componentId: "progressComponent" },
                        ],
                    },
                    {
                        id: 3,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "statusOfProjectsDonutChartComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "statusOfItemsComponent",
                            },
                        ],
                    },
                    {
                        id: 4,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "completionOfProjectsComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "completionOfTrackingItemsComponent",
                            },
                        ],
                    },
                    {
                        id: 5,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "completedProjectsCategoryComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "completedItemsPerCategoryComponent",
                            },
                        ],
                    },
                    {
                        id: 6,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "completedProjectsLineChartComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "completedItemsOverTimeComponent",
                            },
                        ],
                    },
                ],
            },
        ],
    },
    overview: {
        displayTabs: false,
        canEdit: true,
        tabs: [
            {
                id: 0,
                title: "Project Overview",
                rows: [
                    {
                        id: 0,
                        columnCount: 1,
                        columns: [{ columnIndex: 0, componentId: "titleComponent" }],
                    },
                    {
                        id: 1,
                        columnCount: 4,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "numberOfProjectsComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "numberOfCompletedProjectsComponent",
                            },
                            {
                                columnIndex: 2,
                                componentId: "numberOfItemsComponent",
                            },
                            {
                                columnIndex: 3,
                                componentId: "numberOfCompletedItemsComponent",
                            },
                        ],
                    },
                    {
                        id: 2,
                        columnCount: 1,
                        columns: [
                            { columnIndex: 0, componentId: "projectsTableComponent" },
                        ],
                    },
                ],
            },
        ],
    },
    "project-common": {
        tabs: [
            {
                id: 0,
                title: "Dashboard",
                rows: [
                    {
                        id: 0,
                        columnCount: 1,
                        columns: [{ columnIndex: 0, componentId: "titleComponent" }],
                    },
                    {
                        id: 1,
                        columnCount: 4,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "statusComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "numberOfItemsComponent",
                            },
                            {
                                columnIndex: 2,
                                componentId: "numberOfCompletedItemsComponent",
                            },
                            {
                                columnIndex: 3,
                                componentId: "numberOfDueItemsComponent",
                            },
                        ],
                    },
                    {
                        id: 2,
                        columnCount: 3,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "projectTeamComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "projectDurationComponent",
                            },
                            {
                                columnIndex: 2,
                                componentId: "reportingProgressComponent",
                            },
                        ],
                    },
                    {
                        id: 3,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "descriptionComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "managementSummaryComponent",
                            },
                        ],
                    },
                    {
                        id: 4,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "statusDonutComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "completionDonutComponent",
                            },
                        ],
                    },
                    {
                        id: 5,
                        columnCount: 1,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "completedItemsOverTimeComponent",
                            },
                        ],
                    },
                ],
            },
            {
                id: 1,
                title: "Measures",
                rows: [
                    {
                        id: 0,
                        columnCount: 1,
                        columns: [
                            { columnIndex: 0, componentId: "trackingItemsTitleComponent" },
                        ],
                    },
                    {
                        id: 1,
                        columnCount: 4,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "numberofTrackingItemsComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "numberOfCompletedItemsComponent",
                            },
                            {
                                columnIndex: 2,
                                componentId: "countStatusComponent",
                            },
                            {
                                columnIndex: 3,
                                componentId: "countCompletionComponent",
                            },
                        ],
                    },
                    {
                        id: 2,
                        columnCount: 1,
                        columns: [
                            { columnIndex: 0, componentId: "trackingItemsTableComponent" },
                        ],
                    },
                ],
            },
        ],
        displayTabs: true,
        canEdit: true,
    },
};
const PMO_VIEW_CONFIG = {
    dashboard: {
        canEdit: true,
        isAiEnabled: false,
        type: "dashboard",
        displayTabs: false,
        tabs: [
            {
                id: 0,
                title: "Dashboard",
                rows: [
                    {
                        id: 0,
                        columnCount: 1,
                        columns: [{ columnIndex: 0, componentId: "titleComponent" }],
                    },
                    {
                        id: 1,
                        columnCount: 4,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "statusComponent",
                            },
                            { columnIndex: 1, componentId: "numberOfProjectsComponent" },
                            {
                                columnIndex: 2,
                                componentId: "numberOfCompletedProjectsComponent",
                            },
                            { columnIndex: 3, componentId: "numberOfItemsDueComponent" },
                        ],
                    },
                    {
                        id: 2,
                        columnCount: 4,
                        columns: [
                            { columnIndex: 0, componentId: "projectTeamComponent" },
                            { columnIndex: 1, componentId: "projectDurationComponent" },
                            { columnIndex: 2, componentId: "progressComponent" },
                            {
                                columnIndex: 3,
                                componentId: "percentageOfBudgetSpentComponent",
                            },
                        ],
                    },
                    {
                        id: 3,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "statusOfProjectsDonutChartComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "statusOfItemsDonutChartComponent",
                            },
                        ],
                    },
                    {
                        id: 4,
                        columnCount: 2,
                        columns: [
                            { columnIndex: 0, componentId: "programDescriptionComponent" },
                            { columnIndex: 1, componentId: "managementSummaryComponent" },
                        ],
                    },
                ],
            },
        ],
    },
    overview: {
        displayTabs: false,
        canEdit: true,
        tabs: [
            {
                id: 0,
                title: "Project Overview",
                rows: [
                    {
                        id: 0,
                        columnCount: 1,
                        columns: [{ columnIndex: 0, componentId: "titleComponent" }],
                    },
                    {
                        id: 1,
                        columnCount: 4,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "numberOfProjectsComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "numberOfCompletedProjectsComponent",
                            },
                            {
                                columnIndex: 2,
                                componentId: "numberOfItemsComponent",
                            },
                            {
                                columnIndex: 3,
                                componentId: "numberOfCompletedItemsComponent",
                            },
                        ],
                    },
                    {
                        id: 2,
                        columnCount: 1,
                        columns: [
                            { columnIndex: 0, componentId: "projectsTableComponent" },
                        ],
                    },
                ],
            },
        ],
    },
    "project-common": {
        tabs: [
            {
                id: 0,
                title: "Dashboard",
                rows: [
                    {
                        id: 0,
                        columnCount: 1,
                        columns: [{ columnIndex: 0, componentId: "titleComponent" }],
                    },
                    {
                        id: 1,
                        columnCount: 4,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "statusComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "numberofTrackingItemsComponent",
                            },
                            {
                                columnIndex: 2,
                                componentId: "numberOfCompletedItemsComponent",
                            },
                            {
                                columnIndex: 3,
                                componentId: "numberOfDueItemsComponent",
                            },
                        ],
                    },
                    {
                        id: 2,
                        columnCount: 4,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "projectTeamComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "projectDurationComponent",
                            },
                            {
                                columnIndex: 2,
                                componentId: "reportingProgressComponent",
                            },
                            {
                                columnIndex: 3,
                                componentId: "percentageOfBudgetSpentComponent",
                            },
                        ],
                    },
                    {
                        id: 3,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "statusOfItemsDonutChartComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "descriptionComponent",
                            },
                        ],
                    },
                    {
                        id: 4,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "managementSummaryComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "challengesDescriptionComponent",
                            },
                        ],
                    },
                ],
            },
            {
                id: 1,
                title: "Deliverables",
                rows: [
                    {
                        id: 0,
                        columnCount: 1,
                        columns: [
                            { columnIndex: 0, componentId: "trackingItemsTitleComponent" },
                        ],
                    },
                    {
                        id: 1,
                        columnCount: 4,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "statusComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "reportingProgressComponent",
                            },
                            {
                                columnIndex: 2,
                                componentId: "numberofTrackingItemsComponent",
                            },
                            {
                                columnIndex: 3,
                                componentId: "numberOfCompletedItemsComponent",
                            },
                        ],
                    },
                    {
                        id: 2,
                        columnCount: 1,
                        columns: [
                            { columnIndex: 0, componentId: "trackingItemsTableComponent" },
                        ],
                    },
                ],
            },
        ],
        displayTabs: true,
        canEdit: true,
    },
};
const ROLLOUT_VIEW_CONFIG = {
    dashboard: {
        canEdit: true,
        isAiEnabled: false,
        type: "dashboard",
        displayTabs: false,
        tabs: [
            {
                id: 0,
                title: "Dashboard",
                rows: [
                    {
                        id: 0,
                        columnCount: 1,
                        columns: [{ columnIndex: 0, componentId: "titleComponent" }],
                    },
                    {
                        id: 1,
                        columnCount: 4,
                        columns: [
                            { columnIndex: 0, componentId: "statusComponent" },
                            { columnIndex: 1, componentId: "numberOfProjectsComponent" },
                            {
                                columnIndex: 2,
                                componentId: "numberOfTrackingItemsComponent",
                            },
                            { columnIndex: 3, componentId: "numberOfItemsDueComponent" },
                        ],
                    },
                    {
                        id: 2,
                        columnCount: 4,
                        columns: [
                            { columnIndex: 0, componentId: "projectTeamComponent" },
                            { columnIndex: 1, componentId: "projectDurationComponent" },
                            { columnIndex: 2, componentId: "progressComponent" },
                            {
                                columnIndex: 3,
                                componentId: "percentageOfBudgetSpentComponent",
                            },
                        ],
                    },
                    {
                        id: 3,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "statusOfProjectsDonutChartComponent",
                            },
                            { columnIndex: 1, componentId: "statusOfItemsComponent" },
                        ],
                    },
                    {
                        id: 4,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "completionOfProjectsComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "completionOfTrackingItemsComponent",
                            },
                        ],
                    },
                    {
                        id: 5,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "completedProjectsLineChartComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "completedItemsOverTimeComponent",
                            },
                        ],
                    },
                ],
            },
        ],
    },
    overview: {
        displayTabs: false,
        canEdit: true,
        tabs: [
            {
                id: 0,
                title: "Project Overview",
                rows: [
                    {
                        id: 0,
                        columnCount: 1,
                        columns: [{ columnIndex: 0, componentId: "titleComponent" }],
                    },
                    {
                        id: 1,
                        columnCount: 4,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "numberOfProjectsComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "numberOfCompletedProjectsComponent",
                            },
                            {
                                columnIndex: 2,
                                componentId: "numberOfItemsComponent",
                            },
                            {
                                columnIndex: 3,
                                componentId: "numberOfCompletedItemsComponent",
                            },
                        ],
                    },
                    {
                        id: 2,
                        columnCount: 1,
                        columns: [
                            { columnIndex: 0, componentId: "projectsTableComponent" },
                        ],
                    },
                ],
            },
        ],
    },
    "project-common": {
        tabs: [
            {
                id: 0,
                title: "Dashboard",
                rows: [
                    {
                        id: 0,
                        columnCount: 1,
                        columns: [{ columnIndex: 0, componentId: "titleComponent" }],
                    },
                    {
                        id: 1,
                        columnCount: 4,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "statusComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "completionCard",
                            },
                            {
                                columnIndex: 2,
                                componentId: "numberofTrackingItemsComponent",
                            },
                            {
                                columnIndex: 3,
                                componentId: "numberOfCompletedItemsComponent",
                            },
                        ],
                    },
                    {
                        id: 2,
                        columnCount: 4,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "numberOfItemsDueComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "projectTeamComponent",
                            },
                            {
                                columnIndex: 2,
                                componentId: "projectDurationComponent",
                            },
                            {
                                columnIndex: 3,
                                componentId: "reportingProgressComponent",
                            },
                        ],
                    },
                    {
                        id: 3,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "statusDonutComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "completionDonutComponent",
                            },
                        ],
                    },
                    {
                        id: 4,
                        columnCount: 2,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "completedItemsOverTimeComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "managementSummaryComponent",
                            },
                        ],
                    },
                ],
            },
            {
                id: 1,
                title: "Activities",
                rows: [
                    {
                        id: 0,
                        columnCount: 1,
                        columns: [
                            { columnIndex: 0, componentId: "trackingItemsTitleComponent" },
                        ],
                    },
                    {
                        id: 1,
                        columnCount: 4,
                        columns: [
                            {
                                columnIndex: 0,
                                componentId: "numberofTrackingItemsComponent",
                            },
                            {
                                columnIndex: 1,
                                componentId: "numberOfCompletedItemsComponent",
                            },
                            {
                                columnIndex: 2,
                                componentId: "countStatusComponent",
                            },
                            {
                                columnIndex: 3,
                                componentId: "countCompletionComponent",
                            },
                        ],
                    },
                    {
                        id: 2,
                        columnCount: 1,
                        columns: [
                            { columnIndex: 0, componentId: "trackingItemsTableComponent" },
                        ],
                    },
                ],
            },
        ],
        displayTabs: true,
        canEdit: true,
    },
};

const VIEW_NAMES = [
    "reporting-structure",
    "reporting-period",
    "manage-projects-data-model",
    "manage-items-data-model",
    "manage-reporting-data",
    "manage-main-entities",
    "manage-subentities",
    "manage-users",
    "basic-notifications",
    "reporting-use-case",
];

function calculateAverageProgress(subentities) {
    let averageProgress = 0;
    if (!subentities || subentities.length === 0) {
        return { averageProgress: 0 };
    }
    for (const entry of subentities?.map((entry) => entry.progress) ?? []) {
        averageProgress += entry ?? 0;
    }
    averageProgress = Number.parseFloat((averageProgress / subentities?.length).toFixed(2));
    return { averageProgress };
}

function calculateLastUpdateDateForProject(subentities) {
    const sorted = subentities
        .filter((entity) => entity.lastUpdatedAt)
        .sort((a, b) => new Date(b.lastUpdatedAt ?? new Date().toISOString()).getTime() -
        new Date(a.lastUpdatedAt ?? new Date().toISOString()).getTime());
    return {
        lastUpdatedAt: sorted[0]?.lastUpdatedAt ?? null,
        lastUpdatedBy: sorted[0]?.lastUpdatedBy,
    };
}

function calculateNumberOfOverdueSubEntities(subentities) {
    return {
        numberOfOverdueSubEntities: subentities?.filter((entity) => entity.basicItem?.endDate &&
            dayjs(entity.basicItem?.endDate).isBefore(dayjs())).length ?? 0,
    };
}

function calculateOverallDegreeOfCompletion(degreeOfCompletionCounts, completionDefinition) {
    // Find all statuses with count > 0
    const presentStatuses = (completionDefinition ?? [])
        .filter((def) => (degreeOfCompletionCounts[def.name ?? ""] ?? 0) > 0)
        .sort((a, b) => a.order - b.order);
    // Pick the lowest order present status
    const overallDegreeOfCompletion = presentStatuses[0] || { order: 0 };
    return { overallDegreeOfCompletion };
}

function calculateOverallDegreeOfCompletionCount(subentities) {
    const degreeOfCompletionCounts = {};
    for (const entry of subentities?.map((entry) => entry.degreeOfCompletion) ??
        []) {
        if (!entry) {
            continue;
        }
        if (Object.keys(degreeOfCompletionCounts).includes(entry)) {
            degreeOfCompletionCounts[entry]++;
        }
        else {
            degreeOfCompletionCounts[entry] = 1;
        }
    }
    return { degreeOfCompletionCounts };
}

function calculateOverallSeverityCounts(subentities) {
    const severityCounts = {};
    for (const entry of subentities?.map((entry) => entry.severity) ?? []) {
        if (!entry) {
            continue;
        }
        if (Object.keys(severityCounts).includes(entry)) {
            severityCounts[entry]++;
        }
        else {
            severityCounts[entry] = 1;
        }
    }
    return { severityCounts };
}

function calculateOverallStatus(statusCounts, statusDefinition) {
    let overallStatus = { order: 0 };
    Object.entries(statusCounts).forEach(([statusName, count]) => {
        if (count <= 0) {
            return;
        }
        const status = statusDefinition?.find((def) => def.name === statusName);
        if (status && overallStatus.order <= status?.order) {
            overallStatus = status;
        }
    });
    return { overallStatus };
}

function calculateOverallStatusCount(subentities) {
    const statusCounts = {};
    for (const entry of subentities?.map((entry) => entry.status) ?? []) {
        if (!entry) {
            continue;
        }
        if (Object.keys(statusCounts).includes(entry)) {
            statusCounts[entry]++;
        }
        else {
            statusCounts[entry] = 1;
        }
    }
    return { statusCounts };
}

function calculateProjectBudgetDetails(project) {
    const approvedBudget = project?.approvedBudget ?? 0;
    const spentBudget = project.spentBudget ?? 0;
    const percentageOfBudgetSpent = approvedBudget === 0 ? 0 : Math.round((spentBudget / approvedBudget) * 100);
    return percentageOfBudgetSpent;
}

const targetsFor = (entity) => Object.keys(ENTITY_REF_TARGETS)
    .map(getEntityRefTarget)
    .filter((target) => target.entity === entity);
const REPORTING_ITEM_COLUMNS = new Set(targetsFor("reportingItem").map((target) => target.column));
const WRITABLE_REPORTING_ITEM_COLUMNS = new Set(targetsFor("reportingItem")
    .filter((target) => !target.readOnly)
    .map((target) => target.column));
// project.* is namespaced because project shares column names with basicItem;
// every other entity uses the bare column so B1 and the master data tab share
// one react-hook-form key and stay in sync.
const getEntityRefFormKey = (ref) => {
    const target = getEntityRefTarget(ref);
    return target.entity === "project"
        ? `project.${target.column}`
        : target.column;
};
const isEntityRefReadOnly = (ref) => Boolean(getEntityRefTarget(ref).readOnly);
const splitEntityPayload = (flat) => {
    const basicItem = {};
    const reportingItem = {};
    for (const [key, value] of Object.entries(flat)) {
        if (REPORTING_ITEM_COLUMNS.has(key))
            reportingItem[key] = value;
        else
            basicItem[key] = value;
    }
    return { basicItem, reportingItem };
};
// undefined means "the form never had a value for this column", which must not
// blank a stored value. Clearing a field sends "" or null, both of which pass.
const pickWritableReportingItemFields = (input) => Object.fromEntries(Object.entries(input).filter(([key, value]) => WRITABLE_REPORTING_ITEM_COLUMNS.has(key) && value !== undefined));
/**
 * Bare-key form values for every ReportingItem column a question can bind to.
 * The tracking item form seeds these so a bound field renders its stored value
 * instead of rendering empty and overwriting it on save.
 */
const getReportingItemSeedValues = (reportingItem) => {
    if (!reportingItem)
        return {};
    return Object.fromEntries([...REPORTING_ITEM_COLUMNS].map((column) => [column, reportingItem[column]]));
};
// Clearing a bound select submits "", which a Postgres enum rejects.
const coerceEntityEnumBlanks = (entity, values) => {
    const enumColumns = new Set(targetsFor(entity)
        .filter((target) => target.kind === "enum")
        .map((target) => target.column));
    return Object.fromEntries(Object.entries(values).map(([key, value]) => enumColumns.has(key) && value === "" ? [key, null] : [key, value]));
};
const getEntityRefOptions = (ref, configuration, oeChannelIds) => {
    const target = getEntityRefTarget(ref);
    if (target.kind === "enum" || target.kind === "stringArray") {
        const hidden = new Set(target.hiddenValues ?? []);
        return (target.values ?? [])
            .filter((value) => !hidden.has(value))
            .map((value) => ({ value, label: value }));
    }
    if (target.kind === "channel" || target.kind === "channels") {
        const catalog = getDistributionChannels(configuration?.distributionChannelDefinition);
        if (catalog.length === 0) {
            return Array.from(new Set((oeChannelIds ?? []).filter(Boolean))).map((value) => ({ value, label: value }));
        }
        const resolveCatalogId = (raw) => {
            const token = raw.trim();
            if (!token)
                return undefined;
            const exactId = catalog.find((channel) => channel.id === token);
            if (exactId)
                return exactId.id;
            const lowerToken = token.toLowerCase();
            const insensitiveId = catalog.find((channel) => channel.id.toLowerCase() === lowerToken);
            if (insensitiveId)
                return insensitiveId.id;
            const exactLabel = catalog.find((channel) => channel.label === token);
            if (exactLabel)
                return exactLabel.id;
            const insensitiveLabel = catalog.find((channel) => channel.label.toLowerCase() === lowerToken);
            return insensitiveLabel?.id;
        };
        const resolvedIds = Array.from(new Set((oeChannelIds ?? []).map(resolveCatalogId).filter(Boolean)));
        const ids = resolvedIds.length > 0
            ? resolvedIds
            : catalog.map((channel) => channel.id);
        return ids
            .filter((id) => catalog.some((channel) => channel.id === id))
            .map((id) => ({ value: id, label: getChannelLabel(id, catalog) }));
    }
    if (target.kind === "configEnum" && target.configSource) {
        return (configuration?.[target.configSource] ?? []).map((entry) => ({
            value: entry.name,
            label: entry.name,
            color: entry.color,
        }));
    }
    return [];
};

const valueAtPath = (values, path) => path
    .split(".")
    .reduce((current, segment) => current == null
    ? undefined
    : current[segment], values);
/**
 * Evaluates a `conditionalOn` condition against the current full form values.
 *
 * `formValues` is the whole react-hook-form state, so questionnaire answers live
 * under the "formData" key (field names are "formData.<questionId>").
 *
 * Rules:
 *  - No condition → always visible.
 *  - condition.equals is empty → visible when the trigger question has any answer.
 *  - condition.equals is non-empty → visible when the trigger answer matches one of the ids.
 *
 * @param boundFormKeys - form key per bound question id. A bound question answers
 * on its own column, not under formData, so a condition naming one would
 * otherwise read undefined and hide its dependent forever.
 */
function isConditionMet(condition, formValues, boundFormKeys) {
    if (!condition)
        return true;
    const { questionId, equals } = condition;
    const boundKey = boundFormKeys?.[questionId];
    const formData = formValues["formData"];
    const currentValue = boundKey
        ? valueAtPath(formValues, boundKey)
        : formData?.[questionId];
    const hasAnswer = currentValue !== null &&
        currentValue !== undefined &&
        currentValue !== "" &&
        !(Array.isArray(currentValue) && currentValue.length === 0);
    if (!hasAnswer)
        return false;
    if (!equals || equals.length === 0)
        return true;
    if (Array.isArray(currentValue)) {
        return equals.some((e) => currentValue.includes(e));
    }
    return equals.includes(String(currentValue));
}
/** Evaluates whether a question should be visible given the current full form values. */
function isQuestionVisible(question, formValues, boundFormKeys) {
    return isConditionMet(question.conditionalOn, formValues, boundFormKeys);
}
/** Evaluates whether a whole block should be visible given the current full form values. */
function isBlockVisible(block, formValues, boundFormKeys) {
    return isConditionMet(block.conditionalOn, formValues, boundFormKeys);
}

/**
 * Flat view of a tracking item's answers, matching how the assessment dialog's
 * react-hook-form state is shaped: entity-ref-bound questions live under their
 * bare column name (namespaced "project.<column>" for project refs), unbound
 * questions live under "formData.<questionId>".
 */
function flattenFormValues(params) {
    return {
        ...(params.basicItem ?? {}),
        ...(params.reportingItem ?? {}),
        project: { ...(params.project ?? {}) },
        formData: { ...(params.formData ?? {}) },
    };
}
function hasValue(value) {
    if (value === null || value === undefined || value === "")
        return false;
    if (Array.isArray(value) && value.length === 0)
        return false;
    return true;
}
function getQuestionValue(question, flatValues) {
    if (!question.entityRef) {
        return flatValues.formData?.[question.id];
    }
    const key = getEntityRefFormKey(question.entityRef);
    if (key.startsWith("project.")) {
        const projectValues = flatValues.project;
        return projectValues?.[key.slice("project.".length)];
    }
    return flatValues[key];
}
// A block is "complete" once every visible, enabled, mandatory question is
// filled. A block with no mandatory questions is complete once any visible
// question has an answer, and a block with nothing to answer at all is
// vacuously complete — mirrors PimoForm's own tab-completion logic, which the
// Submit button reads to decide whether the assessment may be handed in.
function isBlockComplete(block, flatValues, boundFormKeys = {}) {
    const visibleQuestions = (block.questions ?? []).filter((question) => question.enabled && isQuestionVisible(question, flatValues, boundFormKeys));
    if (visibleQuestions.length === 0)
        return true;
    const mandatoryQuestions = visibleQuestions.filter((q) => q.mandatory);
    if (mandatoryQuestions.length > 0) {
        return mandatoryQuestions.every((q) => hasValue(getQuestionValue(q, flatValues)));
    }
    return visibleQuestions.some((q) => hasValue(getQuestionValue(q, flatValues)));
}
function resolveApplicableBlocks(params) {
    const blocks = (params.blueprint?.blocks ?? []).filter((block) => block.enabled);
    if (blocks.length === 0)
        return undefined;
    // Map question id → form key for entity-ref-bound questions so that block
    // and question conditions pointing at bound fields resolve correctly.
    const boundFormKeys = {};
    for (const block of blocks) {
        for (const question of block.questions ?? []) {
            if (question.entityRef) {
                boundFormKeys[question.id] = getEntityRefFormKey(question.entityRef);
            }
        }
    }
    const flatValues = flattenFormValues(params);
    const applicableBlocks = blocks.filter((block) => isBlockVisible(block, flatValues, boundFormKeys));
    return { applicableBlocks, flatValues, boundFormKeys };
}
/**
 * Derives a tracking item's progress from its questionnaire blocks: the share
 * of applicable (enabled and not conditionally hidden) blocks that are
 * complete, as a whole-number percentage. Mirrors the block completion status
 * shown in the assessment dialog (PimoForm's tab completion indicator), so the
 * percentage always matches what the user sees as done vs. outstanding.
 *
 * Returns `undefined` when the configuration has no blocks at all, so a
 * use-case without the block questionnaire (relying on a manually-entered
 * progress value instead) is left untouched by callers.
 */
function calculateReportingItemProgress(params) {
    const resolved = resolveApplicableBlocks(params);
    if (!resolved)
        return undefined;
    const { applicableBlocks, flatValues, boundFormKeys } = resolved;
    if (applicableBlocks.length === 0)
        return 0;
    const completedBlocks = applicableBlocks.filter((block) => isBlockComplete(block, flatValues, boundFormKeys));
    return Math.round((completedBlocks.length / applicableBlocks.length) * 100);
}
/**
 * Whether any visible mandatory question across the applicable blocks has an
 * answer. Separates "not started" from "in progress": answers to optional
 * questions alone, and blocks complete only vacuously (e.g. the question-less
 * Submission block), do not count as started.
 *
 * Returns `undefined` when the configuration has no blocks at all.
 */
function hasAnsweredMandatoryQuestion(params) {
    const resolved = resolveApplicableBlocks(params);
    if (!resolved)
        return undefined;
    const { applicableBlocks, flatValues, boundFormKeys } = resolved;
    return applicableBlocks.some((block) => (block.questions ?? []).some((question) => question.enabled &&
        question.mandatory &&
        isQuestionVisible(question, flatValues, boundFormKeys) &&
        hasValue(getQuestionValue(question, flatValues))));
}

/**
 * Scans a list of reporting items and determines the worst semaphore level
 * for semaphoreStatus1, semaphoreStatus2 and semaphoreStatus3 separately.
 * Priority: high > medium > low. Returns an object with properties status1,status2,status3
 * containing the matching level object or undefined.
 */
function calculateSemaphoreStatus(reportingItems, semaphoreStatusDefinition) {
    const def = Array.isArray(semaphoreStatusDefinition)
        ? semaphoreStatusDefinition[0]
        : undefined;
    if (!def || !reportingItems || reportingItems.length === 0)
        return { status1: undefined, status2: undefined, status3: undefined };
    const values1 = reportingItems
        .map((ri) => ri.semaphoreStatus1)
        .filter(Boolean);
    const values2 = reportingItems
        .map((ri) => ri.semaphoreStatus2)
        .filter(Boolean);
    const values3 = reportingItems
        .map((ri) => ri.semaphoreStatus3)
        .filter(Boolean);
    const matchesAny = (values, name) => name != null && values.some((v) => v === name);
    const status1 = matchesAny(values1, def.high?.name)
        ? def.high
        : matchesAny(values1, def.medium?.name)
            ? def.medium
            : matchesAny(values1, def.low?.name)
                ? def.low
                : undefined;
    const status2 = matchesAny(values2, def.high?.name)
        ? def.high
        : matchesAny(values2, def.medium?.name)
            ? def.medium
            : matchesAny(values2, def.low?.name)
                ? def.low
                : undefined;
    const status3 = matchesAny(values3, def.high?.name)
        ? def.high
        : matchesAny(values3, def.medium?.name)
            ? def.medium
            : matchesAny(values3, def.low?.name)
                ? def.low
                : undefined;
    // Determine overall worst: high > medium > low
    const overall = status1 === def.high || status2 === def.high || status3 === def.high
        ? def.high
        : status1 === def.medium ||
            status2 === def.medium ||
            status3 === def.medium
            ? def.medium
            : status1 === def.low || status2 === def.low || status3 === def.low
                ? def.low
                : undefined;
    return { status1, status2, status3, overall };
}

/**
 * Given an array of { category, count }, returns an array where each count is accumulated.
 * Example:
 * Input:  [{ category: '2024-01-01', count: 1 }, { category: '2024-02-01', count: 1 }]
 * Output: [{ category: '2024-01-01', count: 1 }, { category: '2024-02-01', count: 2 }]
 */
function countAccumulatedItemsOverReportingCycle(items) {
    let runningTotal = 0;
    return items.map((item) => {
        runningTotal += item.count;
        return { category: item.category, count: runningTotal };
    });
}

function countCompletedReportingItems(reportingItems, configuration) {
    if (!reportingItems ||
        reportingItems.length === 0 ||
        !configuration?.degreeOfCompletionDefinition) {
        return 0;
    }
    const degreeOfCompletionDefinition = getReviewStageName(configuration, "submitted");
    if (!degreeOfCompletionDefinition) {
        return 0;
    }
    return reportingItems.filter((subentity) => subentity.degreeOfCompletion === degreeOfCompletionDefinition).length;
}

function countCompletedItemsPerCategoryLevel(reportingItems, configuration) {
    const categoryCounts = {};
    if (!configuration.dropdownFieldOneDefinition ||
        !configuration.degreeOfCompletionDefinition ||
        !reportingItems?.length) {
        return [];
    }
    const degreeOfCompletionDefinition = getReviewStageName(configuration, "submitted");
    const flattedReportingItemsWithCategory = reportingItems
        ?.map((i) => ({
        ...i,
        dropdownFieldOne: i.basicItem?.dropdownFieldOne,
    }))
        .filter(
    // An unresolved name must not match an item with no degree of completion
    // either — both are `undefined`, and every untouched item would
    // otherwise count as complete. Bars still render, at 0.
    (item) => !!degreeOfCompletionDefinition &&
        item?.degreeOfCompletion === degreeOfCompletionDefinition);
    const existingCategories = configuration.dropdownFieldOneDefinition?.map((cat) => cat.name) ?? [];
    for (const entry of flattedReportingItemsWithCategory?.map((entry) => entry?.dropdownFieldOne) ?? []) {
        if (!entry || !existingCategories.includes(entry)) {
            continue;
        }
        if (Object.keys(categoryCounts).includes(entry)) {
            categoryCounts[entry]++;
        }
        else {
            categoryCounts[entry] = 1;
        }
    }
    const result = existingCategories.map((category) => ({
        label: category,
        value: categoryCounts[category] ?? 0,
        color: configuration.dropdownFieldOneDefinition?.find((item) => item.name === category)?.color,
    }));
    return result;
}

const countCompletedItemsOverReportingCycle = (reportingItems, configuration) => {
    if (!reportingItems ||
        reportingItems.length === 0 ||
        !configuration?.degreeOfCompletionDefinition ||
        !configuration?.reportingCycle?.startReportingDate ||
        !configuration?.reportingCycle?.endReportingDate ||
        !configuration?.reportingCycle?.frequency ||
        !configuration?.reportingCycle?.unit) {
        return [];
    }
    const degreeOfCompletionDefinition = getReviewStageName(configuration, "submitted");
    const { frequency, unit } = configuration.reportingCycle;
    let startReportingDate = dayjs(configuration.reportingCycle.startReportingDate);
    const endReportingDate = dayjs(configuration.reportingCycle.endReportingDate);
    const completedItemsOverReportingCycle = [];
    while (startReportingDate.isBefore(endReportingDate)) {
        const endReportingCycleDate = startReportingDate.add(frequency, unit);
        const completedItems = reportingItems.filter((item) => {
            const completionDate = dayjs(item.completionDate);
            if (!completionDate)
                return false;
            return (
            // An unresolved name must not match an item with no degree of
            // completion either — both are `undefined`.
            !!degreeOfCompletionDefinition &&
                item.degreeOfCompletion === degreeOfCompletionDefinition &&
                completionDate.isAfter(startReportingDate) &&
                !completionDate.isAfter(endReportingCycleDate));
        }).length;
        startReportingDate = startReportingDate.add(frequency, unit);
        completedItemsOverReportingCycle.push({
            category: dayjs(startReportingDate).format("YYYY-MM"),
            count: completedItems,
        });
    }
    return completedItemsOverReportingCycle;
};

function countOverdueProjects(projects) {
    return projects.filter((project) => project.endDate && dayjs(project.endDate).isBefore(dayjs(), "day")).length;
}

function countSemaphoreStatusesOfReportingItems(reportingItems) {
    const statusCounts = {};
    for (const statusName of reportingItems?.map((item) => item._calculated?.semaphoreStatus?.name) ?? []) {
        if (!statusName) {
            continue;
        }
        statusCounts[statusName] = (statusCounts[statusName] ?? 0) + 1;
    }
    return { statusCounts };
}

function calculateCustomFieldSumsForItems(reportingItems) {
    const customFieldSums = {};
    if (reportingItems.length === 0) {
        return { customFieldSums: {} };
    }
    for (const reportingItem of reportingItems ?? []) {
        for (const customField of reportingItem.customFields ?? []) {
            if (customField.customFieldDefinition?.type !== "Number") {
                continue;
            }
            const key = String(customField.customFieldDefinition?.id ?? "");
            if (!key) {
                continue;
            }
            const rawValue = String(customField.value ?? "").trim();
            const parsedValue = rawValue === "" ? 0 : Number.parseFloat(rawValue);
            if (rawValue !== "" && !Number.isFinite(parsedValue)) {
                continue;
            }
            customFieldSums[key] = (customFieldSums[key] ?? 0) + parsedValue;
        }
    }
    return { customFieldSums };
}
function calculateCustomFieldAveragesForItems(reportingItems) {
    if (reportingItems.length === 0) {
        return { customFieldAverages: {} };
    }
    const customFieldSums = {};
    for (const reportingItem of reportingItems ?? []) {
        for (const customField of reportingItem.customFields ?? []) {
            if (customField.customFieldDefinition?.type !== "Number") {
                continue;
            }
            const key = String(customField.customFieldDefinition?.id ?? "");
            if (!key) {
                continue;
            }
            const rawValue = String(customField.value ?? "").trim();
            const parsedValue = rawValue === "" ? 0 : Number.parseFloat(rawValue);
            if (rawValue !== "" && !Number.isFinite(parsedValue)) {
                continue;
            }
            customFieldSums[key] = (customFieldSums[key] ?? 0) + parsedValue;
        }
    }
    const customFieldAverages = {};
    for (const key in customFieldSums) {
        customFieldAverages[key] = customFieldSums[key] / reportingItems.length;
    }
    return { customFieldAverages };
}
function calculateCustomFieldSumsForProjects(projects) {
    const customFieldSums = {};
    if (projects.length === 0) {
        return { customFieldSums: {} };
    }
    for (const project of projects ?? []) {
        for (const customField of project.customFields ?? []) {
            if (customField.customFieldDefinition?.type !== "Number") {
                continue;
            }
            const key = String(customField.customFieldDefinition?.id ?? "");
            if (!key) {
                continue;
            }
            const rawValue = String(customField.value ?? "").trim();
            const parsedValue = rawValue === "" ? 0 : Number.parseFloat(rawValue);
            if (rawValue !== "" && !Number.isFinite(parsedValue)) {
                continue;
            }
            customFieldSums[key] = (customFieldSums[key] ?? 0) + parsedValue;
        }
    }
    return { customFieldSums };
}
function calculateCustomFieldAveragesForProjects(projects) {
    if (projects.length === 0) {
        return { customFieldAverages: {} };
    }
    const customFieldSums = {};
    for (const project of projects ?? []) {
        for (const customField of project.customFields ?? []) {
            if (customField.customFieldDefinition?.type !== "Number") {
                continue;
            }
            const key = String(customField.customFieldDefinition?.id ?? "");
            if (!key) {
                continue;
            }
            const rawValue = String(customField.value ?? "").trim();
            const parsedValue = rawValue === "" ? 0 : Number.parseFloat(rawValue);
            if (rawValue !== "" && !Number.isFinite(parsedValue)) {
                continue;
            }
            customFieldSums[key] = (customFieldSums[key] ?? 0) + parsedValue;
        }
    }
    const customFieldAverages = {};
    for (const key in customFieldSums) {
        customFieldAverages[key] = customFieldSums[key] / projects.length;
    }
    return { customFieldAverages };
}

// Hidden record types stay in the database but count nowhere.
const isCountableReportingItem = (item) => Boolean(item) &&
    !item.isChannelHeader &&
    !isHiddenRecordType(item.basicItem?.recordType);
const excludeChannelHeaders = (items) => (items ?? []).filter(isCountableReportingItem);

function getProjectCompletionDates(project, configuration) {
    if (!project || !project.reportingItems) {
        return null;
    }
    const countableItems = excludeChannelHeaders(project.reportingItems);
    if (countableItems.length === 0) {
        return null;
    }
    // A stored completionDate is not sufficient on its own: an earlier OE save
    // could have stamped it without the App ever being reviewed.
    const isProjectCompleted = countableItems.every((item) => item.completionDate && isReportingItemReviewed(item, configuration));
    const latest = isProjectCompleted
        ? countableItems
            .map((item) => item.completionDate)
            .filter(Boolean)
            .map((date) => dayjs(date))
            .filter((d) => d.isValid())
            .sort((a, b) => b.valueOf() - a.valueOf())[0]
        : null;
    return latest ? latest.toISOString() : null;
}

/**
 * generic solution to determine reporting days
 * @param params details for the reporting cycle, and the last updated date per oe
 * @returns reporting status
 */
function getReportingStatus(params) {
    const { endReportingDateInStringFormat, lastUpdatedDateInStringFormat, conditionUponWhichToReturnGreenByDefault, frequency, unit, } = params;
    if (!endReportingDateInStringFormat || !frequency || !unit) {
        return {
            color: COLORS[2],
            icon: ICONS[2],
            status: "Overdue",
            contrastTextColor: CONTRAST_TEXT_COLORS[2],
            muiIcon: "SentimentDissatisfied",
        };
    }
    if (!lastUpdatedDateInStringFormat) {
        return {
            color: COLORS[1],
            icon: ICONS[1],
            status: "Pending",
            contrastTextColor: CONTRAST_TEXT_COLORS[1],
            muiIcon: "SentimentSatisfied",
        };
    }
    if (conditionUponWhichToReturnGreenByDefault) {
        return {
            color: COLORS[0],
            icon: ICONS[0],
            status: "Updated",
            contrastTextColor: CONTRAST_TEXT_COLORS[0],
            muiIcon: "SentimentSatisfiedAlt",
        };
    }
    const endReportingDate = dayjs(endReportingDateInStringFormat);
    const lastUpdatedDate = dayjs(lastUpdatedDateInStringFormat);
    const startOfCurrentReportingCycle = endReportingDate.subtract(frequency, unit);
    const daysInCurrentReportingCycle = endReportingDate.diff(startOfCurrentReportingCycle, "days");
    const startOfPreviousReportingCycle = endReportingDate.subtract(2 * frequency, unit);
    const daysSinceStartOfPreviousCycle = endReportingDate.diff(startOfPreviousReportingCycle, "days");
    switch (true) {
        /**
         * case 1: if the lastUpdateDate is within the current cycle, the difference between the endDate and the last date will be less than the days in the current cycle
         * case 2: if the lastUpdateDate is within the previous cycle, the difference between the endDate and the last date will be less than the days in the previous cycle + current cycle
         * case 3: if the lastUpdateDate is not within the current or previous cycle, the difference between the endDate and the last date will be greater than the days in the previous cycle + current cycle
         */
        case dayjs(endReportingDate).diff(lastUpdatedDate, "days", true) <=
            daysInCurrentReportingCycle:
            return {
                color: COLORS[0],
                icon: ICONS[0],
                status: "Updated",
                contrastTextColor: CONTRAST_TEXT_COLORS[0],
                muiIcon: "SentimentSatisfiedAlt",
            };
        case dayjs(endReportingDate).diff(lastUpdatedDate, "days", true) <=
            daysSinceStartOfPreviousCycle:
            return {
                color: COLORS[1],
                icon: ICONS[1],
                status: "Pending",
                contrastTextColor: CONTRAST_TEXT_COLORS[1],
                muiIcon: "SentimentSatisfied",
            };
        default:
            return {
                color: COLORS[2],
                icon: ICONS[2],
                status: "Overdue",
                contrastTextColor: CONTRAST_TEXT_COLORS[2],
                muiIcon: "SentimentDissatisfied",
            };
    }
}

const DC_RELEVANCE_BLOCK_TITLE = "dc relevance";
/**
 * Whether the "DC Relevance" block specifically has been submitted, independent
 * of the reporting item's overall status. Activating a later block reopens an
 * already-submitted item (see reconcileSubmittedItemsAfterBlueprintChange),
 * dropping its overall degreeOfCompletion back to "in progress"/"not started"
 * while leaving DC Relevance's own answer locked and untouched
 * (`lockedBlockIds`) — display code that only cares about DC Relevance (e.g.
 * report/overview tables) must not gate on the item's overall status, or it
 * would wrongly show "not reviewed" for a block that is still locked in.
 */
function isDcRelevanceBlockReviewed(reportingItem, configuration) {
    if (!configuration)
        return false;
    const dcRelevanceBlockId = (configuration.trackingItemForms?.[0]?.blocks ?? []).find((block) => block.title?.trim().toLowerCase() === DC_RELEVANCE_BLOCK_TITLE)?.id;
    return (isReportingItemReviewed(reportingItem, configuration) ||
        (!!dcRelevanceBlockId &&
            !!reportingItem.lockedBlockIds?.includes(dcRelevanceBlockId)));
}

/**
 * Re-evaluates reporting items that are currently at the submitted stage
 * against a configuration's current blueprint — e.g. right after an admin
 * activates a new block. An item whose recomputed progress is no longer 100%
 * is no longer actually complete, so it needs to drop back to
 * "in progress"/"not started".
 *
 * Callers should leave `lockedBlockIds` untouched when applying the returned
 * downgrades: it must keep reflecting the blocks answered at the item's last
 * submission, so only the newly-required block(s) become editable again.
 */
function reconcileSubmittedItemsAfterBlueprintChange(configuration, submittedItems) {
    const submittedName = getReviewStageName(configuration, "submitted");
    if (!submittedName)
        return [];
    const downgrades = [];
    for (const item of submittedItems) {
        const progress = calculateReportingItemProgress({
            blueprint: configuration.trackingItemForms?.[0],
            basicItem: item.basicItem,
            reportingItem: item.reportingItem,
            project: item.project,
            formData: item.formData,
        });
        // undefined means this configuration has no questionnaire blocks at all
        // (nothing to reconcile); 100 means still fully complete.
        if (progress === undefined || progress === 100)
            continue;
        const nextName = progress > 0
            ? getReviewStageName(configuration, "inProgress")
            : getReviewStageName(configuration, "notStarted");
        if (!nextName)
            continue;
        downgrades.push({
            id: item.id,
            progress,
            degreeOfCompletion: nextName,
            completionDate: null,
        });
    }
    return downgrades;
}

const debounceHandlers = {};
/**
 * Debounced field validation utility.
 *
 * This function validates a form field or an item in an array of fields using the provided validation checks.
 * It supports debouncing to avoid excessive validation calls during rapid input changes.
 *
 * @param {Object} params - The parameters for field validation.
 * @param {string} [params.fieldName] - The name of the field to validate.
 * @param {string} [params.iterableFieldName] - The name of the iterable field (with "{idx}" as a placeholder for index).
 *   Use this when validating items in an array of fields. For example, if validating the third item in an array field called "emails",
 *   set iterableFieldName to "emails.{idx}" and idx to 2. The function will replace "{idx}" with the index, resulting in "emails.2" as the error key.
 *   Multiple "{idx}" placeholders can be used for nested arrays, and the idx parameter can be an array of indices to replace them in order.
 * @param {string} [params.customFieldKey] - Optional custom key for field-specific validation.
 * @param {number | number[]} [params.idx] - Index for iterable fields. Can be a single number or an array of numbers for multiple placeholders in the field name.
 * @param {unknown} [params.value] - The value to validate.
 * @param {Record<string, ExtendedFieldError | FieldError>} params.errors - Current error state object.
 * @param {(errors: Record<string, ExtendedFieldError | FieldError>) => void} params.setErrors - Callback to update the error state.
 * @param {string} [params.tabContainingErrors] - Optional tab identifier containing errors.
 * @param {("required" | "maxLength" )[]} [params.checks] - List of validation checks to perform. If omitted, all checks are run.
 *
 * The function clears any existing debounce timer for the field, then sets a new timer.
 * On timer execution, it removes previous errors for the field, runs the specified validation checks
 * ("required", "maxLength") and updates the error state via setErrors.
 * - "required": Checks if the field is filled.
 * - "maxLength": Checks if the value does not exceed the maximum allowed length.
 */
const checkField = ({ fieldName, iterableFieldName, customFieldKey, idx, value, errors, setErrors, tabContainingErrors, checks, }) => {
    const resolvedFieldName = fieldName ??
        (iterableFieldName ? resolveIndex(iterableFieldName, idx) : undefined);
    if (!resolvedFieldName) {
        return;
    }
    if (debounceHandlers[resolvedFieldName]) {
        clearTimeout(debounceHandlers[resolvedFieldName]);
    }
    debounceHandlers[resolvedFieldName] = setTimeout(() => {
        if (fieldName) {
            delete errors[fieldName];
        }
        else if (iterableFieldName && idx != null) {
            delete errors[resolvedFieldName];
        }
        if (!checks || checks.includes("required")) {
            checkRequiredField({
                fieldName,
                iterableFieldName,
                idx,
                value,
                errors,
                tabContainingErrors,
            });
        }
        if (!checks || checks.includes("maxLength")) {
            checkFieldLength({
                fieldName,
                iterableFieldName,
                idx,
                value,
                errors,
                customFieldKey,
                tabContainingErrors,
            });
        }
        setErrors(errors);
    }, 250);
};
/**
 * Validates that a field value is present (required field check).
 *
 * If the value is falsy (e.g., undefined, null, empty string), this function adds a "required" error
 * to the errors object for the specified field or iterable field item.
 *
 * @param {Object} params - The parameters for required field validation.
 * @param {string} [params.fieldName] - The name of the field to validate.
 * @param {string} [params.iterableFieldName] - The name of the iterable field (with "{idx}" as a placeholder for index).
 * @param {number} [params.idx] - Index for iterable fields.
 * @param {unknown} [params.value] - The value to check for presence.
 * @param {Record<string, ExtendedFieldError | FieldError>} params.errors - The error state object to update.
 * @param {string} [params.tabContainingErrors] - Optional tab identifier containing errors.
 *
 * If the value is present, no action is taken. Otherwise, an error is added to the errors object.
 */
const checkRequiredField = ({ fieldName, iterableFieldName, idx, value, errors, tabContainingErrors, }) => {
    const hasValue = value !== undefined && value !== null && value !== "";
    if (hasValue) {
        return;
    }
    if (fieldName != null) {
        errors[fieldName] = {
            type: "required",
            message: "This field is required",
            tabContainingErrors,
        };
    }
    else if (iterableFieldName != null && idx != null) {
        errors[resolveIndex(iterableFieldName, idx)] = {
            type: "required",
            message: "This field is required",
            tabContainingErrors,
        };
    }
};
/**
 * Validates that a field value does not exceed its maximum allowed length.
 *
 * Converts the value to a string and checks its length against the configured maximum for the field.
 * If the value exceeds the limit, a "maxLength" error is added to the errors object for the field or iterable field item.
 *
 * @param {Object} params - The parameters for length validation.
 * @param {string} [params.fieldName] - The name of the field to validate.
 * @param {string} [params.iterableFieldName] - The name of the iterable field (with "{idx}" as a placeholder for index).
 * @param {number} [params.idx] - Index for iterable fields.
 * @param {unknown} [params.value] - The value to check length for.
 * @param {Record<string, ExtendedFieldError | FieldError>} params.errors - The error state object to update.
 * @param {string} [params.customFieldKey] - Optional custom key for field-specific length limits.
 * @param {string} [params.tabContainingErrors] - Optional tab identifier containing errors.
 *
 * If the value is empty or within the allowed length, no action is taken. Otherwise, an error is added to the errors object.
 */
const checkFieldLength = ({ fieldName, iterableFieldName, idx, value, errors, customFieldKey, tabContainingErrors, }) => {
    if (!value) {
        return;
    }
    // convert value to string to check the length
    const v = new String(value);
    if (fieldName != null) {
        const maxLength = CHARACTER_LIMITATIONS[customFieldKey ? customFieldKey : fieldName];
        if (v.length > maxLength) {
            errors[fieldName] = {
                type: "maxLength",
                message: `Must be ${maxLength} characters or less`,
                tabContainingErrors,
            };
        }
    }
    else if (iterableFieldName != null && idx != null) {
        const maxLength = CHARACTER_LIMITATIONS[customFieldKey ? customFieldKey : iterableFieldName];
        if (v.length > maxLength) {
            errors[resolveIndex(iterableFieldName, idx)] = {
                type: "maxLength",
                message: `Must be ${maxLength} characters or less`,
                tabContainingErrors,
            };
        }
    }
};
/**
 * Utility function to resolve the error key for an iterable field item based on the provided field name and index.
 *
 * This function takes an iterable field name with a "{idx}" placeholder and replaces it with the provided index or indices.
 * If no index is provided, it returns the original iterable field name.
 *
 * @param {string} iterableFieldName - The name of the iterable field containing the "{idx}" placeholder.
 * @param {number | number[]} [idx] - The index or indices to replace in the field name. Can be a single number or an array of numbers for multiple placeholders.
 * @returns {string} The resolved field name with the index(es) replaced, or the original field name if no index is provided.
 */
const resolveIndex = (iterableFieldName, idx) => {
    if (idx === undefined) {
        return iterableFieldName;
    }
    if (Array.isArray(idx)) {
        let name = iterableFieldName;
        idx.forEach((i) => {
            name = name.replace("{idx}", `${i}`);
        });
        return name;
    }
    return iterableFieldName.replace("{idx}", `${idx}`);
};

/**
 * Returns either "white" or "black" for the best contrast
 * against the given background color.
 * @param hexColor - The background color in hex format (e.g. "#ff0000" or "ff0000")
 */
function getContrastColor(hexColor) {
    // Remove leading "#" if present
    const color = hexColor.replace(/^#/, "");
    // Parse R, G, B values
    const r = parseInt(color.substring(0, 2), 16);
    const g = parseInt(color.substring(2, 4), 16);
    const b = parseInt(color.substring(4, 6), 16);
    // Calculate the relative luminance (per W3C formula)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    // Return white for dark backgrounds, black for light backgrounds
    return luminance > 0.5 ? "black" : "white";
}

function getFieldValidation(configuration, fieldName, purpose) {
    return configuration?.fieldsDefinition
        ?.filter((f) => f.purpose === purpose)
        ?.find((f) => f.fieldName === fieldName && f.enabled === true)?.isMandatory;
}

function validateBasicItemForm(item, configuration, localizedMessages) {
    const errors = {};
    for (const field of configuration?.fieldsDefinition?.filter((f) => f.purpose === "itemBasic" && f.enabled && f.isMandatory) ?? []) {
        const fieldValue = item[field.fieldName];
        if (fieldValue === undefined || fieldValue === "" || fieldValue === null) {
            errors[`${field.fieldName}`] = {
                type: "required",
                message: localizedMessages?.fieldRequired || "This field is required",
                tabContainingErrors: `Manage ${ITEM_NAMES.find((name) => name.single === configuration?.projectName)?.plural ?? "Tracking Items"}`,
            };
        }
        checkFieldLength({
            fieldName: field.fieldName,
            customFieldKey: `basicItems.{idx}.${field.fieldName}`,
            value: fieldValue,
            errors: errors,
        });
        if (item.endDate && !dayjs(item.endDate).isValid()) {
            errors[`endDate`] = {
                type: "invalid",
                message: localizedMessages?.dateNotValid || "This date is not valid",
                tabContainingErrors: `Manage ${ITEM_NAMES.find((name) => name.single === configuration?.projectName)?.plural ?? "Tracking Items"}`,
            };
        }
        if (item.startDate && !dayjs(item.startDate).isValid()) {
            errors[`startDate`] = {
                type: "invalid",
                message: localizedMessages?.dateNotValid || "This date is not valid",
                tabContainingErrors: `Manage ${ITEM_NAMES.find((name) => name.single === configuration?.projectName)?.plural ?? "Tracking Items"}`,
            };
        }
        if (item.endDate &&
            item.startDate &&
            new Date(item.endDate).getTime() < new Date(item.startDate).getTime()) {
            errors[`endDate`] = {
                type: "invalid",
                message: localizedMessages?.endDateAfterStartDate ||
                    "End date should be after start date",
                tabContainingErrors: `Manage ${ITEM_NAMES.find((name) => name.single === configuration?.projectName)?.plural ?? "Tracking Items"}`,
            };
        }
    }
    const definedEntries = Object.entries(errors).filter(([, v]) => v !== undefined);
    return Object.fromEntries(definedEntries);
}

function validateBasicProjectForm(project, configuration, localizedMessages) {
    const errors = {};
    for (const field of configuration?.fieldsDefinition?.filter((f) => f.purpose === "projectBasic" && f.enabled && f.isMandatory) ?? []) {
        const fieldValue = project[field.fieldName];
        if (fieldValue === undefined || fieldValue === "" || fieldValue === null) {
            errors[`${field.fieldName}`] = {
                type: "required",
                message: localizedMessages?.fieldRequired || "This field is required",
                tabContainingErrors: `Manage ${PROJECT_NAMES.find((name) => name.single === configuration?.projectName)?.plural ?? "Projects"}`,
            };
        }
        checkFieldLength({
            fieldName: field.fieldName,
            customFieldKey: `projects.{idx}.${field.fieldName}`,
            value: fieldValue,
            errors: errors,
        });
        if (project.endDate && !dayjs(project.endDate).isValid()) {
            errors[`endDate`] = {
                type: "invalid",
                message: localizedMessages?.dateNotValid || "This date is not valid",
                tabContainingErrors: `Manage ${PROJECT_NAMES.find((name) => name.single === configuration?.projectName)?.plural ?? "Projects"}`,
            };
        }
        if (project.startDate && !dayjs(project.startDate).isValid()) {
            errors[`startDate`] = {
                type: "invalid",
                message: localizedMessages?.dateNotValid || "This date is not valid",
                tabContainingErrors: `Manage ${PROJECT_NAMES.find((name) => name.single === configuration?.projectName)?.plural ?? "Projects"}`,
            };
        }
        if (project.endDate &&
            project.startDate &&
            new Date(project.endDate).getTime() <
                new Date(project.startDate).getTime()) {
            errors[`endDate`] = {
                type: "invalid",
                message: localizedMessages?.endDateAfterStartDate ||
                    "End date should be after start date",
                tabContainingErrors: `Manage ${PROJECT_NAMES.find((name) => name.single === configuration?.projectName)?.plural ?? "Projects"}`,
            };
        }
    }
    const definedEntries = Object.entries(errors).filter(([, v]) => v !== undefined);
    return Object.fromEntries(definedEntries);
}

// Validates the tracking-item form blueprint (distribution only), mutating the
// shared `errors` record so an invalid blueprint blocks the settings save.
function validateTrackingItemForms(params) {
    const { configuration, errors, tab, requiredMessage } = params;
    if (configuration.useCase !== "distribution") {
        return;
    }
    const require = (key) => {
        errors[key] = {
            type: "required",
            message: requiredMessage,
            tabContainingErrors: tab,
        };
    };
    const invalid = (key, message) => {
        errors[key] = { type: "invalid", message, tabContainingErrors: tab };
    };
    // Blank once HTML tags are stripped, mirroring the backend sanitizePlainText
    // step so a tag-only label like "<b></b>" can't pass then persist as empty.
    const isBlank = (value) => !value || value.replace(/<[^>]*>/g, "").trim() === "";
    const seenBlockIds = new Set();
    const seenQuestionIds = new Set();
    const seenBlockCodes = new Set();
    const seenQuestionCodes = new Set();
    const normalizedCode = (code) => code?.trim().toLowerCase() ?? "";
    (configuration.trackingItemForms ?? []).forEach((form, formIdx) => {
        (form.blocks ?? []).forEach((block, blockIdx) => {
            const blockKey = `trackingItemForms.${formIdx}.blocks.${blockIdx}`;
            // Only enabled items are validated; a switched-off block/question can be
            // parked with a blank or draft title/label without blocking the save.
            if (block.enabled) {
                if (isBlank(block.title)) {
                    require(`${blockKey}.title`);
                }
                checkFieldLength({
                    iterableFieldName: "trackingItemForms.{idx}.blocks.{idx}.title",
                    value: block.title,
                    idx: [formIdx, blockIdx],
                    tabContainingErrors: tab,
                    errors,
                });
            }
            if (block.id && seenBlockIds.has(block.id)) {
                invalid(`${blockKey}.id`, "Block identifiers must be unique");
            }
            if (block.id)
                seenBlockIds.add(block.id);
            const blockCode = normalizedCode(block.code);
            if (blockCode) {
                if (seenBlockCodes.has(blockCode)) {
                    invalid(`${blockKey}.code`, "Block codes must be unique");
                }
                seenBlockCodes.add(blockCode);
            }
            (block.questions ?? []).forEach((question, questionIdx) => {
                const questionKey = `${blockKey}.questions.${questionIdx}`;
                if (question.enabled) {
                    if (isBlank(question.label)) {
                        require(`${questionKey}.label`);
                    }
                    checkFieldLength({
                        iterableFieldName: "trackingItemForms.{idx}.blocks.{idx}.questions.{idx}.label",
                        value: question.label,
                        idx: [formIdx, blockIdx, questionIdx],
                        tabContainingErrors: tab,
                        errors,
                    });
                }
                if (question.id && seenQuestionIds.has(question.id)) {
                    invalid(`${questionKey}.id`, "Question identifiers must be unique");
                }
                if (question.id)
                    seenQuestionIds.add(question.id);
                const questionCode = normalizedCode(question.code);
                if (questionCode) {
                    if (seenQuestionCodes.has(questionCode)) {
                        invalid(`${questionKey}.code`, "Question codes must be unique");
                    }
                    seenQuestionCodes.add(questionCode);
                }
                const isSelect = question.answerType === "singleSelect" ||
                    question.answerType === "multiSelect";
                // A bound question draws its options from the entity_ref registry, so it
                // stores none and the editor offers no way to add any.
                if (isSelect && question.enabled && !question.entityRef) {
                    if ((question.options ?? []).length === 0) {
                        invalid(`${questionKey}.options`, "Add at least one option");
                    }
                    (question.options ?? []).forEach((option, optionIdx) => {
                        if (isBlank(option.label)) {
                            require(`${questionKey}.options.${optionIdx}.label`);
                        }
                        checkFieldLength({
                            iterableFieldName: "trackingItemForms.{idx}.blocks.{idx}.questions.{idx}.options.{idx}.label",
                            value: option.label,
                            idx: [formIdx, blockIdx, questionIdx, optionIdx],
                            tabContainingErrors: tab,
                            errors,
                        });
                    });
                }
            });
        });
    });
}

function validateConfiguration(configuration, projects, basicItems, toolUsers, clientUsers, localizedMessages, notification) {
    const errors = {};
    const requiredMessage = localizedMessages?.fieldRequired || "This field is required";
    const invalidDateMessage = localizedMessages?.dateNotValid || "This date is not valid";
    const endBeforeStartMessage = localizedMessages?.endDateAfterStartDate ||
        "End date should be after start date";
    const setError = (key, type, message, tabContainingErrors) => {
        errors[key] = {
            type,
            message,
            tabContainingErrors: tabContainingErrors ?? "",
        };
    };
    const requireField = (key, tab) => setError(key, "required", requiredMessage, tab);
    // Reporting Structure
    if (!configuration.programName) {
        requireField("programName", VIEW_NAMES[0]);
    }
    checkFieldLength({
        fieldName: "programName",
        value: configuration.programName,
        errors,
    });
    checkFieldLength({
        fieldName: "programOwner",
        value: configuration.programOwner,
        errors,
    });
    if (!configuration.projectName) {
        requireField("projectName", VIEW_NAMES[0]);
    }
    if (!configuration.itemName) {
        requireField("itemName", VIEW_NAMES[0]);
    }
    // Distribution channels: reject blank/whitespace-only and duplicate labels so unusable channels never persist.
    if (configuration.useCase === "distribution") {
        const seenChannelLabels = new Set();
        (configuration.distributionChannelDefinition ?? []).forEach((channel, idx) => {
            const label = (channel.label ?? "").trim();
            if (label === "") {
                setError(`distributionChannelDefinition.${idx}.label`, "required", requiredMessage, VIEW_NAMES[9]);
                return;
            }
            if (seenChannelLabels.has(label.toLowerCase())) {
                setError(`distributionChannelDefinition.${idx}.label`, "invalid", "Channel names must be unique", VIEW_NAMES[9]);
            }
            seenChannelLabels.add(label.toLowerCase());
        });
    }
    // Reporting Period
    if (!configuration.reportingCycle?.unit) {
        requireField("unit", VIEW_NAMES[1]);
    }
    if (!configuration.reportingCycle?.startReportingDate) {
        requireField("startReportingDate", VIEW_NAMES[1]);
    }
    if (!configuration.reportingCycle?.endReportingDate) {
        requireField("endReportingDate", VIEW_NAMES[1]);
    }
    if (configuration.reportingCycle?.startReportingDate &&
        !dayjs(configuration.reportingCycle?.startReportingDate).isValid()) {
        setError("startReportingDate", "invalid", invalidDateMessage, VIEW_NAMES[1]);
    }
    if (configuration.reportingCycle?.endReportingDate &&
        !dayjs(configuration.reportingCycle?.endReportingDate).isValid()) {
        setError("endReportingDate", "invalid", invalidDateMessage, VIEW_NAMES[1]);
    }
    if (configuration.reportingCycle?.endReportingDate &&
        configuration.reportingCycle?.startReportingDate &&
        new Date(configuration.reportingCycle?.endReportingDate).getTime() <
            new Date(configuration.reportingCycle?.startReportingDate).getTime()) {
        setError("endReportingDate", "invalid", endBeforeStartMessage, VIEW_NAMES[1]);
    }
    if (!configuration.reportingCycle?.frequency) {
        requireField("frequency", VIEW_NAMES[1]);
    }
    if (configuration.reportingCycle?.unit === "week" &&
        !configuration.reportingCycle?.weeklyReportingDay) {
        requireField("weeklyReportingDay", VIEW_NAMES[1]);
    }
    if (configuration.reportingCycle?.unit === "month" &&
        !configuration.reportingCycle?.monthlyReportingDay) {
        requireField("monthlyReportingDay", VIEW_NAMES[1]);
    }
    if (configuration.reportingCycle?.unit === "year" &&
        !configuration.reportingCycle?.yearlyReportingDay) {
        requireField("yearlyReportingDay", VIEW_NAMES[1]);
    }
    if (configuration.fieldsDefinition?.find((f) => f.fieldName === "category" && f.enabled && f.label.trim() === "")) {
        requireField("categoryDefinition", VIEW_NAMES[2]);
    }
    if (configuration.fieldsDefinition?.find((f) => f.fieldName === "dropdownFieldOne" && f.enabled && f.label.trim() === "")) {
        requireField("dropdownFieldOneDefinition", VIEW_NAMES[3]);
    }
    // Custom fields
    configuration.customFields?.forEach((field, index) => {
        if (field.flag === "delete")
            return;
        if (!field.label || field.label.trim() === "") {
            requireField(`customFields.${index}.label`, VIEW_NAMES[4]);
        }
        checkFieldLength({
            iterableFieldName: "customFields.{idx}.label",
            value: field.label,
            idx: index,
            tabContainingErrors: VIEW_NAMES[4],
            errors,
        });
        if (field.type === "Select" && field.enabled) {
            field.options?.forEach((option, optionIndex) => {
                if (option.name.trim() === "") {
                    requireField(`customFields.${index}.options.${optionIndex}.name`, VIEW_NAMES[4]);
                }
                checkFieldLength({
                    iterableFieldName: "customFields.{idx}.options.{idx}.name",
                    value: option.name,
                    idx: [index, optionIndex],
                    tabContainingErrors: VIEW_NAMES[4],
                    errors,
                });
            });
        }
    });
    // Dynamic tracking-item form blocks (distribution use case).
    validateTrackingItemForms({
        configuration,
        errors,
        tab: VIEW_NAMES[4],
        requiredMessage,
    });
    const fieldsDef = configuration.fieldsDefinition ?? [];
    const validateListWithFieldDef = (list, listKey, tabTitle, errorMessage = "All names must be filled in", fieldInConfiguration) => {
        list?.forEach((item, idx) => {
            const fieldDef = fieldInConfiguration
                ? fieldsDef.find((f) => f.fieldInConfiguration === fieldInConfiguration)
                : undefined;
            if (fieldInConfiguration && !fieldDef?.enabled) {
                return;
            }
            if (item.name.trim() === "") {
                setError(`${listKey}.${idx}.name`, "required", errorMessage, tabTitle);
            }
            const maxLength = listKey === "degreeOfCompletionDefinition" ? 25 : 15;
            if (item.name.length > maxLength) {
                setError(`${listKey}.${idx}.name`, "invalid", `Name must be less than ${maxLength} characters`, tabTitle);
            }
        });
    };
    validateListWithFieldDef(configuration.categoryDefinition, "categoryDefinition", VIEW_NAMES[2], "All category names must be filled in", "categoryDefinition");
    validateListWithFieldDef(configuration.dropdownFieldOneDefinition, "dropdownFieldOneDefinition", VIEW_NAMES[3], "All fields must be filled in", "dropdownFieldOneDefinition");
    validateListWithFieldDef(configuration.secondCategoryDefinition, "secondCategoryDefinition", VIEW_NAMES[2], "All category names must be filled in", "secondCategoryDefinition");
    validateListWithFieldDef(configuration.statusDefinition, "statusDefinition", VIEW_NAMES[4], "All status names must be filled in", "statusDefinition");
    validateListWithFieldDef(configuration.degreeOfCompletionDefinition, "degreeOfCompletionDefinition", VIEW_NAMES[4], "All degree of completion names must be filled in", "degreeOfCompletionDefinition");
    projects?.forEach((project, idx) => {
        if (project.flag === "delete")
            return;
        for (const field of fieldsDef.filter((f) => f.purpose === "projectBasic" && f.enabled && f.isMandatory)) {
            const value = project[field.fieldName];
            switch (field.type) {
                case "Text":
                case "Number":
                    checkFieldLength({
                        iterableFieldName: `projects.{idx}.${field.fieldName}`,
                        idx,
                        errors,
                        value,
                        tabContainingErrors: VIEW_NAMES[5],
                    });
                    checkRequiredField({
                        iterableFieldName: `projects.{idx}.${field.fieldName}`,
                        idx,
                        errors,
                        value,
                        tabContainingErrors: VIEW_NAMES[5],
                    });
                    break;
                case "Date":
                    if (project[field.fieldName] &&
                        !dayjs(project[field.fieldName]).isValid()) {
                        setError(`projects.${idx}.${field.fieldName}`, "invalid", invalidDateMessage, VIEW_NAMES[5]);
                        break;
                    }
            }
        }
        if (project.endDate &&
            project.startDate &&
            new Date(project.endDate).getTime() <
                new Date(project.startDate).getTime()) {
            setError(`projects.${idx}.endDate`, "invalid", endBeforeStartMessage, VIEW_NAMES[5]);
        }
    });
    basicItems?.forEach((basicItem, idx) => {
        if (basicItem.flag === "delete")
            return;
        for (const field of fieldsDef.filter((f) => f.purpose === "itemBasic" && f.enabled && f.isMandatory)) {
            const value = basicItem[field.fieldName];
            if (value === undefined || value === "" || value === null) {
                setError(`basicItems.${idx}.${field.fieldName}`, "required", requiredMessage, VIEW_NAMES[6]);
            }
            if (!basicItem.projectIds?.length) {
                setError(`basicItems.${idx}.projectIds`, "invalid", "This field is required", VIEW_NAMES[6]);
            }
            if (basicItem.endDate && !dayjs(basicItem.endDate).isValid()) {
                setError(`basicItems.${idx}.endDate`, "invalid", invalidDateMessage, VIEW_NAMES[6]);
            }
            if (basicItem.startDate && !dayjs(basicItem.startDate).isValid()) {
                setError(`basicItems.${idx}.startDate`, "invalid", invalidDateMessage, VIEW_NAMES[6]);
            }
            checkFieldLength({
                iterableFieldName: `basicItem.{idx}.budget`,
                idx,
                errors,
                value,
                tabContainingErrors: VIEW_NAMES[8],
            });
            checkFieldLength({
                iterableFieldName: `basicItems.{idx}.${field.fieldName}`,
                value: value,
                idx,
                errors,
            });
        }
        if (basicItem.customId && basicItem.customId?.length > 15) {
            setError(`basicItems.${idx}.customId`, "invalid", "Custom ID must be less than 15 characters", VIEW_NAMES[6]);
        }
        if (basicItem.endDate &&
            basicItem.startDate &&
            new Date(basicItem.endDate).getTime() <
                new Date(basicItem.startDate).getTime()) {
            setError(`basicItems.${idx}.endDate`, "invalid", endBeforeStartMessage, VIEW_NAMES[6]);
        }
    });
    toolUsers?.forEach((user, item) => {
        if (user.flag === "delete")
            return;
        if ((clientUsers?.filter((u) => u.email === user.email && u.id !== user.id)
            ?.length ?? 0) > 0) {
            setError(`toolUsers.${item}.email`, "invalid", "This email is already used by a client user", VIEW_NAMES[7]);
        }
        if (!user.email) {
            errors[`toolUsers.${item}.email`] = {
                type: "required",
                message: "This field is required",
                tabContainingErrors: VIEW_NAMES[7],
            };
        }
        if (!user.permissions?.role) {
            errors[`toolUsers.${item}.permissions.role`] = {
                type: "required",
                message: "This field is required",
                tabContainingErrors: VIEW_NAMES[7],
            };
        }
    });
    // validate only if the notification is active.
    if (notification?.active) {
        if (!notification?.title) {
            requireField("notification.title", VIEW_NAMES[8]);
        }
        if (!notification?.text) {
            requireField("notification.text", VIEW_NAMES[8]);
        }
        if (!notification?.recipients || notification.recipients.length === 0) {
            requireField("notification.recipients", VIEW_NAMES[8]);
        }
    }
    if (notification?.title && notification.title.length > 64) {
        setError("notification.title", "maxLength", "The notification title must not be longer than 64 characters", VIEW_NAMES[8]);
    }
    if (notification?.text && notification.text.length > 256) {
        setError("notification.text", "maxLength", "The notification text must not be longer than 256 characters", VIEW_NAMES[8]);
    }
    return errors;
}

function validateReportUpdateForm(data, configuration, localizedMessages) {
    if (configuration?.useCase === "distribution") {
        return {};
    }
    const errors = {};
    const requiredMessage = localizedMessages?.fieldRequired || "This field is required";
    const setError = (path, message = requiredMessage) => {
        errors[path] = { type: "required", message };
    };
    const isRequired = (fieldKey, area) => getFieldValidation(configuration, fieldKey, area) === true;
    // project-level fields
    const projectChecks = [
        {
            present: Boolean(data.managementSummary),
            value: data.managementSummary,
            field: "managementSummary",
            path: "managementSummary",
        },
        {
            present: Boolean(data.challenges),
            value: data.challenges,
            field: "challenges",
            path: "challenges",
        },
        {
            present: Boolean(data.achievements),
            value: data.achievements,
            field: "achievements",
            path: "achievements",
        },
        {
            present: Boolean(data.nextSteps),
            value: data.nextSteps,
            field: "nextSteps",
            path: "nextSteps",
        },
        {
            present: data.spentBudget != null,
            value: data.spentBudget,
            field: "spentBudget",
            path: "spentBudget",
            nullIsAllowed: false,
        },
    ];
    for (const check of projectChecks) {
        if (!check.present && isRequired(check.field, "projectReporting")) {
            setError(check.path);
        }
        checkFieldLength({
            fieldName: check.field,
            value: check.value,
            errors: errors,
        });
    }
    for (const cf of data.customFields ?? []) {
        if (cf.customFieldDefinition?.mandatory &&
            (cf.value == null || cf.value === "")) {
            setError(`customFields.[${data.customFields?.indexOf(cf)}].value`);
        }
        checkFieldLength({
            customFieldKey: `customFields.${cf.customFieldDefinition.type}`,
            fieldName: `customFields.[${data.customFields?.indexOf(cf)}].value`,
            value: cf.value,
            errors: errors,
        });
    }
    // item-level checks
    for (const reportingItem of data.reportingItems ?? []) {
        if (reportingItem?.isChannelHeader ||
            isHiddenRecordType(reportingItem?.basicItem?.recordType)) {
            continue;
        }
        const idx = data.reportingItems?.indexOf(reportingItem) ?? 0;
        const basePath = (p) => `reportingItems.[${idx}].${p}`;
        const iterableBasePath = (p) => `reportingItems.[{idx}].${p}`;
        const isEmpty = (v) => v == null || v === "";
        // status: required either when not pmo, or when pmo & overall level
        const statusRequired = isEmpty(reportingItem?.status) &&
            isRequired("status", "itemReporting") &&
            (configuration?.useCase !== "pmo" ||
                (configuration?.useCase === "pmo" &&
                    configuration?.statusReportingLevel === "overall"));
        if (statusRequired) {
            setError(basePath("status"));
        }
        // when pmo & category level -> three semaphore fields required
        if (configuration?.useCase === "pmo" &&
            configuration.statusReportingLevel === "category") {
            if (isEmpty(reportingItem?.semaphoreStatus1)) {
                setError(basePath("semaphoreStatus1"));
            }
            if (isEmpty(reportingItem?.semaphoreStatus2)) {
                setError(basePath("semaphoreStatus2"));
            }
            if (isEmpty(reportingItem?.semaphoreStatus3)) {
                setError(basePath("semaphoreStatus3"));
            }
        }
        // completion
        if (isEmpty(reportingItem?.degreeOfCompletion) &&
            isRequired("completion", "itemReporting")) {
            setError(basePath("degreeOfCompletion"));
        }
        // progress
        if (reportingItem?.progress == null &&
            isRequired("progress", "itemReporting")) {
            setError(basePath("progress"));
        }
        // comment
        if (isEmpty(reportingItem?.comment) &&
            isRequired("comment", "itemReporting")) {
            setError(basePath("comment"));
        }
        checkFieldLength({
            iterableFieldName: iterableBasePath("comment"),
            idx,
            value: reportingItem?.comment,
            errors: errors,
        });
        // attachments (special message)
        if ((!reportingItem?.attachments || reportingItem.attachments.length === 0) &&
            isRequired("attachments", "itemReporting")) {
            setError(basePath("attachments"), "At least one attachment is required");
        }
        let customFieldIdx = 0;
        for (const cf of reportingItem.customFields ?? []) {
            if (cf.customFieldDefinition?.mandatory &&
                (cf.value == null || cf.value === "")) {
                setError(basePath(`customFields.[${reportingItem.customFields?.indexOf(cf)}].value`));
            }
            checkFieldLength({
                customFieldKey: `customFields.${cf.customFieldDefinition.type}`,
                iterableFieldName: basePath(`customFields.[${customFieldIdx}].value`),
                idx,
                value: cf.value,
                errors: errors,
            });
            customFieldIdx++;
        }
    }
    const definedEntries = Object.entries(errors).filter(([, v]) => v !== undefined);
    return Object.fromEntries(definedEntries);
}

const ALL_REFS = Object.keys(ENTITY_REF_TARGETS);
const REF_BY_LOWER = new Map(ALL_REFS.map((ref) => [ref.toLowerCase(), ref]));
const REFS_BY_COLUMN = (() => {
    const map = new Map();
    for (const ref of ALL_REFS) {
        const column = ENTITY_REF_TARGETS[ref].column.toLowerCase();
        map.set(column, [...(map.get(column) ?? []), ref]);
    }
    return map;
})();
// vmd-backend compiles with strictNullChecks off, where narrowing on a boolean
// discriminant does not work. An explicit guard narrows in every consumer.
const isEntityRefFailure = (resolution) => !resolution.ok;
/** Excel cells arrive with pasted backticks, non-breaking spaces and stray padding. */
const normalizeEntityRef = (raw) => raw.replace(/[`'"]/g, "").replace(/ /g, " ").replace(/\s+/g, " ").trim();
// Longest column first so "otherDistributionChannels" is not shadowed by the
// "distributionChannel" it contains.
const suggestRefs = (value) => {
    const lower = value.toLowerCase();
    const columns = [...REFS_BY_COLUMN.keys()].sort((a, b) => b.length - a.length);
    const hits = [];
    for (const column of columns) {
        if (!lower.includes(column))
            continue;
        for (const ref of REFS_BY_COLUMN.get(column) ?? []) {
            if (!hits.includes(ref))
                hits.push(ref);
        }
    }
    return hits;
};
/** Returns undefined for a blank cell, which means the question is not bound. */
const resolveEntityRef = (raw) => {
    const value = normalizeEntityRef(String(raw ?? ""));
    if (!value)
        return undefined;
    const exact = REF_BY_LOWER.get(value.toLowerCase());
    if (exact)
        return { ok: true, ref: exact };
    // A bare column does not say which entity it writes, and `name` and `customId`
    // sit on more than one, so the sheet always spells out the entity prefix.
    const byColumn = REFS_BY_COLUMN.get(value.toLowerCase()) ?? [];
    if (byColumn.length > 0)
        return { ok: false, reason: "unqualified", suggestions: byColumn };
    return { ok: false, reason: "unknown", suggestions: suggestRefs(value) };
};
const listEntityRefs = () => [...ALL_REFS].sort();

dayjs.extend(utc);
function getReportingDetails(reportingDetails) {
    const { unit = "week", frequency } = reportingDetails;
    if (!frequency) {
        reportingDetails.switchReportingCycleDate = null;
        reportingDetails.startReportingCycleDate = null;
        reportingDetails.endReportingCycleDate = null;
        reportingDetails.startReportingDate = null;
        reportingDetails.endReportingDate = null;
        reportingDetails.unit = "week";
        return;
    }
    const today = dayjs.utc();
    const baseDate = today.isAfter(dayjs.utc(reportingDetails.startReportingDate))
        ? today
        : dayjs.utc(reportingDetails.startReportingDate);
    if (unit === "week") {
        const todaysDay = baseDate.day();
        if (todaysDay > Number(reportingDetails.weeklyReportingDay)) {
            reportingDetails.switchReportingCycleDate = baseDate
                .add(frequency, unit)
                .day(Number(reportingDetails.weeklyReportingDay))
                .endOf("day")
                .toISOString();
        }
        else {
            reportingDetails.switchReportingCycleDate = baseDate
                .add(frequency - 1, unit)
                .day(Number(reportingDetails.weeklyReportingDay))
                .endOf("day")
                .toISOString();
        }
    }
    else if (unit === "month") {
        const monthlyDay = Number(reportingDetails.monthlyReportingDay);
        if (monthlyDay === 31 || monthlyDay === 30) {
            reportingDetails.switchReportingCycleDate = dayjs
                .utc(baseDate)
                .add(frequency - 1, unit)
                .endOf("month")
                .endOf("day")
                .toISOString();
        }
        else {
            if (today.date() > monthlyDay) {
                reportingDetails.switchReportingCycleDate = dayjs
                    .utc(baseDate)
                    .add(frequency, unit)
                    .date(monthlyDay)
                    .endOf("day")
                    .toISOString();
            }
            else {
                reportingDetails.switchReportingCycleDate = dayjs
                    .utc(baseDate)
                    .add(frequency - 1, unit)
                    .date(monthlyDay)
                    .endOf("day")
                    .toISOString();
            }
        }
    }
    else {
        reportingDetails.switchReportingCycleDate = dayjs
            .utc(reportingDetails.yearlyReportingDay)
            .toISOString();
    }
    reportingDetails.startReportingCycleDate = baseDate
        .endOf("day")
        .toISOString();
    reportingDetails.endReportingCycleDate = dayjs
        .utc(reportingDetails.switchReportingCycleDate)
        .toISOString();
}

function calculateItemSemaphoreStatus(reportingItem, semaphoreStatusDefinition) {
    // since the definitions are the same for all items, we just take the first one
    // if we decide to make the colors customizable this logic needs to be adapted too
    const def = Array.isArray(semaphoreStatusDefinition)
        ? semaphoreStatusDefinition[0]
        : undefined;
    if (!def)
        return undefined;
    const statuses = [
        reportingItem.semaphoreStatus1,
        reportingItem.semaphoreStatus2,
        reportingItem.semaphoreStatus3,
    ].filter(Boolean);
    const matchesAny = (name) => {
        return name != null && statuses.some((s) => s === name);
    };
    if (matchesAny(def.high?.name)) {
        return def.high;
    }
    if (matchesAny(def.medium?.name)) {
        return def.medium;
    }
    if (matchesAny(def.low?.name)) {
        return def.low;
    }
    return undefined;
}

export { ASSESSMENT_CONFIG, AUDIT_CONFIG, AUDIT_VIEW_CONFIG, CHARACTER_LIMITATIONS, DC_RELEVANCE_BLOCK_TITLE, DEFAULT_CONFIG, DISTRIBUTION_CONFIG, PMO_CONFIG, PMO_VIEW_CONFIG, ROLLOUT_CONFIG, ROLLOUT_VIEW_CONFIG, VIEW_NAMES, buildAccessString, calculateAverageProgress, calculateCustomFieldAveragesForItems, calculateCustomFieldAveragesForProjects, calculateCustomFieldSumsForItems, calculateCustomFieldSumsForProjects, calculateItemSemaphoreStatus, calculateLastUpdateDateForProject, calculateNumberOfOverdueSubEntities, calculateOverallCompletionDegreeCounts, calculateOverallDegreeOfCompletion, calculateOverallDegreeOfCompletionCount, calculateOverallSeverityCounts, calculateOverallStatus, calculateOverallStatusCount, calculateProjectBudgetDetails, calculateReportingItemProgress, calculateSemaphoreStatus, calculateTotalBudgetDetails, canUserPerformOperation, checkField, checkFieldLength, checkRequiredField, coerceEntityEnumBlanks, countAccumulatedItemsOverReportingCycle, countCompletedItemsOverReportingCycle, countCompletedItemsPerCategory, countCompletedItemsPerCategoryLevel, countCompletedProjectsOverReportingCycle, countCompletedProjectsPerCategory, countCompletedReportingItems, countCompletionDegreesOfProjects, countOverdueProjects, countReviewedBusinessApplications, countSemaphoreStatusesOfProjects, countSemaphoreStatusesOfReportingItems, countStatusesOfProjects, determineUserRole, excludeChannelHeaders, getAssessmentConfig, getAuditConfig, getContrastColor, getDefaultConfig, getDistributionConfig, getEntityRefFormKey, getEntityRefOptions, getFieldValidation, getPmoConfig, getProjectCompletionDates, getReportingDetails, getReportingItemSeedValues, getReportingStatus, getReviewStageName, getRolloutConfig, hasAnsweredMandatoryQuestion, isBlockVisible, isCountableReportingItem, isDcRelevanceBlockReviewed, isEntityRefFailure, isEntityRefReadOnly, isQuestionVisible, isReportingItemReviewed, listEntityRefs, normalizeEntityRef, parsePermission, pickWritableReportingItemFields, reconcileSubmittedItemsAfterBlueprintChange, resolveEntityRef, splitEntityPayload, validateBasicItemForm, validateBasicProjectForm, validateConfiguration, validateReportUpdateForm, validateTrackingItemForms };
